// DOM HUD: scores, gauges, kill feed, scoreboard, minimap, banners.

import { batteryDps } from "./battery.js";
import { roadDistance, ROAD_HALF_WIDTH } from "./world.js";
import { insigniaHTML, insigniaDef, insigniaURL } from "./insignia.js";
import {
  CONV_MIN_M,
  CONV_MAX_M,
  CONV_STEP_M,
  convergenceAdvice,
  convergenceFor,
  convergenceSketch,
  factoryConvergence,
} from "./harmonize.js";
import { keymap, codeHint } from "./keybindings.js";

const $ = (id) => document.getElementById(id);

const TEAM_CLASS = ["crimson", "cobalt"];
const TEAM_LABEL = ["CRIMSON", "COBALT"];

// Wire stat key → what a pilot would call it. Used wherever a career gate
// faces the player (paint locks, mission rewards) — "15 nightKills" is a
// database column, "15 night kills" is a goal.
const STAT_LABEL = {
  kills: "air kills",
  ships: "ships sunk",
  ground: "ground kills",
  trucks: "supply trucks destroyed",
  nightKills: "night kills",
  gunKills: "gun-mount kills",
  wingKills: "wingman kills",
  wins: "round wins",
  aces: "aces downed",
  challenges: "daily missions completed",
};
const statLabel = (key) => STAT_LABEL[key] ?? key;

// Write-if-changed setters for everything the HUD redraws per frame or per
// snapshot. A textContent write swaps in a fresh text node and dirties layout
// even when it carries the very digits the gauge already shows; className = ""
// then add() restyles the chip twice for nothing; an inline style re-set to
// its own value still queues a recalc. Forty readouts doing that at 60 Hz
// were a seventh of the main thread on the air map at phone speed
// (docs/client-performance-audit.md, item 1). Comparing first is a DOM read,
// never a layout read, so these cost nothing when the gauge holds still —
// which is most gauges, most frames.
export function setText(el, s) {
  s = String(s);
  el._hudHTML = undefined; // a text write retires any markup memo below
  if (el.textContent !== s) el.textContent = s;
}

export function setClass(el, c) {
  if (el.className !== c) el.className = c;
}

// Markup can't be compared against the live DOM (the browser re-serialises
// it its own way), so it remembers the string it last wrote.
export function setHTML(el, html) {
  if (el._hudHTML === html) return;
  el._hudHTML = html;
  el.innerHTML = html;
}

// Inline styles remember what they last wrote too: the live value comes back
// re-serialised ("0.500" reads as "0.5"), so comparing against it would write
// every frame anyway. Only for elements the HUD alone styles.
export function setStyle(el, prop, v) {
  const memo = el._hudStyle ?? (el._hudStyle = {});
  if (memo[prop] === v) return;
  memo[prop] = v;
  el.style[prop] = v;
}

// The viewport, read once per resize. innerWidth/innerHeight and every
// clientWidth/clientHeight force a synchronous layout when asked after the
// frame's first HUD write, and the frame used to ask several times — the page
// laid out up to two and a half times a frame. Everything that places a
// marker by pixels reads these instead.
export const viewport = { w: innerWidth || 1280, h: innerHeight || 720 };
addEventListener("resize", () => {
  viewport.w = innerWidth || 1280;
  viewport.h = innerHeight || 720;
});

// A BOMBER CREW pick rides the unit picker's `gk` like every other seat:
// 21 + 4·airframe + station. The server's seat numbers are per airframe (seat
// 0 is a Fortress's tail and a Heinkel's dorsal gun), so the card carries
// both, and every airframe's stations keep a gk of their own. Four slots an
// airframe is room to spare over the two seats any of them mounts.
export const TURRET_GK = 21;
const TURRET_GK_STRIDE = 4;
export const turretGk = (plane, seat) => TURRET_GK + TURRET_GK_STRIDE * plane + seat;
/** The {plane, seat} a crew card's gk picks (gk >= TURRET_GK). */
export const turretOfGk = (gk) => ({
  plane: Math.floor((gk - TURRET_GK) / TURRET_GK_STRIDE),
  seat: (gk - TURRET_GK) % TURRET_GK_STRIDE,
});

// Place an absolutely positioned marker at an NDC point by transform alone:
// the compositor moves it, layout never hears about it. The element's CSS
// pins it at the viewport's top-left corner (left/top 0) and this centres it
// on the point, the job its old translate(-50%, -50%) did.
function placeAtNDC(el, ndcX, ndcY, extra = "") {
  const x = ((ndcX + 1) / 2) * viewport.w;
  const y = ((1 - ndcY) / 2) * viewport.h;
  setStyle(el, "transform", `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%)${extra}`);
}

export class Hud {
  constructor() {
    this.el = {
      hud: $("hud"),
      scoreCrimson: $("score-crimson"),
      scoreCobalt: $("score-cobalt"),
      timer: $("timer"),
      zone: $("zone-state"),
      spd: $("g-spd"),
      alt: $("g-alt"),
      thr: $("g-thr"),
      bombs: $("g-bombs"),
      bombsGauge: $("bombs-gauge"),
      gGauge: $("g-gauge"),
      gload: $("g-load"),
      stall: $("stall-warn"),
      stallGauge: $("stall-gauge"),
      stallRibbon: $("stall-ribbon"),
      bombRing: $("bomb-ring"),
      autopilot: $("autopilot-ind"),
      navalPanel: $("naval-panel"),
      navalShell: $("naval-shell"),
      navalTorp: $("naval-torp"),
      navalSmoke: $("naval-smoke"),
      navalRepair: $("naval-repair"),
      navalBoost: $("naval-boost"),
      navalDC: $("naval-dc"),
      navalDot: $("naval-dot"),
      trimGauge: $("trim-gauge"),
      trimTick: $("trim-tick"),
      game: $("game"),
      hp: $("hp-fill"),
      killfeed: $("killfeed"),
      message: $("message"),
      joinScreen: $("join-screen"),
      scoreboard: $("scoreboard"),
      scoreboardCrimson: $("scoreboard-crimson"),
      scoreboardCobalt: $("scoreboard-cobalt"),
      protect: $("protect-banner"),
      cursor: $("aim-cursor"),
      stickCentre: $("stick-centre"),
      leadPip: $("lead-pip"),
      bombsight: $("bombsight"),
      respawnDeck: $("respawn-deck"),
      respawnDaily: $("respawn-daily"),
      unitTabs: $("unit-tabs"),
      unitSelect: $("unit-select"),
      paintRow: $("paint-row"),
      gunsRow: $("guns-row"),
      insigniaRow: $("insignia-row"),
      insigniaBtn: $("insignia-btn"),
      deviceHint: $("device-hint"),
      warning: $("warning-banner"),
      crosshair: $("crosshair"),
      reload: $("reload-indicator"),
      battery: $("turret-status"),
      minimap: $("minimap"),
      plotTable: $("plot-table"),
      plotCanvas: $("plot-canvas"),
      plotLoupe: $("plot-loupe"),
      plotBanner: $("plot-banner"),
      plotStatus: $("plot-status"),
      plotReadout: $("plot-readout"),
      plotReadoutRows: $("plot-readout-rows"),
      plotPrimary: $("plot-primary"),
      plotReaim: $("plot-reaim"),
      plotExit: $("plot-exit"),
      chat: $("chat"),
      chatfeed: $("chatfeed"),
      chatEntry: $("chat-entry"),
      chatInput: $("chat-input"),
    };
    this.minimapCtx = this.el.minimap.getContext("2d");
    this.plotCtx = this.el.plotCanvas.getContext("2d");
    this.plotLoupeCtx = this.el.plotLoupe.getContext("2d");
    this.plotView = null; // {cx, cz, r, size} — set each plot draw, for hit-testing
    this.mapBase = null;
    this.flagshipKind = null; // set during fleet-action rounds
    this._msgTimer = null;
    this._hitTimer = null;

    this.flash = document.createElement("div");
    this.flash.id = "damage-flash";
    document.body.appendChild(this.flash);

    // Directional damage flash (roadmap #56): four screen-edge arcs, one lit
    // toward the quarter the fire came from. Built here so it needs no markup.
    this.dmgDir = document.createElement("div");
    this.dmgDir.id = "dmg-dir";
    this.dmgArcs = [];
    for (const side of ["front", "right", "back", "left"]) {
      const arc = document.createElement("div");
      arc.className = "dmg-arc dmg-" + side;
      this.dmgDir.appendChild(arc);
      this.dmgArcs.push(arc);
    }
    document.body.appendChild(this.dmgDir);
    this._dmgArcAt = [0, 0, 0, 0]; // per-quadrant throttle timestamps

    // The naval conning chips double as buttons (the touch path to the X/R/
    // T/G keys; also clickable). main.js assigns onNavalAction and gates it
    // on actually conning a warship.
    this.onNavalAction = null;
    for (const [el, kind] of [
      ["navalShell", "ammo"],
      ["navalDC", "dc"],
      ["navalSmoke", "smoke"],
      ["navalRepair", "repair"],
      ["navalBoost", "boost"],
    ]) {
      this.el[el]?.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        this.onNavalAction?.(kind);
      });
    }

    // G-physiology veils: a black vignette that tunnels in under sustained
    // positive G (grey-out → black-out) and a red wash under negative G
    // (red-out). Driven each frame by gForce(); opacity lags the load so the
    // veil builds and clears like the pilot's vision, not the accelerometer.
    this.gveil = document.createElement("div");
    this.gveil.id = "g-veil";
    this.rveil = document.createElement("div");
    this.rveil.id = "g-redout";
    document.body.appendChild(this.gveil);
    document.body.appendChild(this.rveil);
    this._gGrey = 0;
    this._gRed = 0;
    this._gShown = 1;
    this._stalling = false;

    // Localized-damage subsystem panel: compact bars (engine, wings, fuel,
    // pilot) above the health bar that gray out as each subsystem is shot up.
    // Built in JS so it needs no index.html change; shown only once damaged.
    this.subsys = document.createElement("div");
    this.subsys.id = "subsys";
    this.subsys.classList.add("hidden");
    this._subBars = {};
    for (const [key, label] of [["engine", "ENG"], ["wing", "WING"], ["fuel", "FUEL"], ["pilot", "PLT"]]) {
      const cell = document.createElement("div");
      cell.className = "subsys-cell";
      const lab = document.createElement("label");
      lab.textContent = label;
      const bar = document.createElement("div");
      bar.className = "subsys-bar";
      const fill = document.createElement("div");
      fill.className = "subsys-fill";
      bar.appendChild(fill);
      cell.append(lab, bar);
      this.subsys.appendChild(cell);
      this._subBars[key] = fill;
    }
    this.el.hud.appendChild(this.subsys);

    // Pilot-wound vignette: a dark-red tunnel when the pilot is hit, distinct
    // from the G-induced grey-out veil.
    this.pveil = document.createElement("div");
    this.pveil.id = "pilot-veil";
    document.body.appendChild(this.pveil);

    // Jets-era seeker ring (docs/early-jets-era.md): a boresight circle at
    // screen center that dims while caged, blinks amber while the head is
    // tracking, and burns solid red at full lock — plus the rails/flares
    // chips underneath. Built in JS so it needs no index.html change; shown
    // only on an era server for a plane that carries the kit.
    this.seeker = document.createElement("div");
    this.seeker.id = "seeker";
    this.seeker.classList.add("hidden");
    this.seekerRing = document.createElement("div");
    this.seekerRing.className = "seeker-ring";
    this.seekerLabel = document.createElement("div");
    this.seekerLabel.className = "seeker-label";
    this.seekerChips = document.createElement("div");
    this.seekerChips.className = "seeker-chips";
    this.seeker.append(this.seekerRing, this.seekerLabel, this.seekerChips);
    this.el.hud.appendChild(this.seeker);
    this._seekerMode = null;

    window.addEventListener("resize", () => {
      if (!this.el.plotTable.classList.contains("hidden")) this.resizePlot();
    });
  }

  show() {
    this.el.joinScreen.classList.add("hidden");
    this.el.joinScreen.classList.remove("respawning");
    this.el.hud.classList.remove("hidden");
    document.body.classList.remove("spawn-open");
  }

  setScore(sc, fleet = null, bk = null) {
    // Wind arrives as windX/windZ from snapshots and wxx/wxz in the welcome.
    this.wind = { x: sc.windX ?? sc.wxx ?? 0, z: sc.windZ ?? sc.wxz ?? 0 };
    this.brickton = bk; // the minimap marks the town while it's the objective
    if (bk) {
      // Battle of Brickton: the raid's side reads how much of the town
      // burns, the defense's how many raiders it has downed.
      const burn = `🔥${Math.round(bk.burn * 100)}%`;
      const down = `✈${bk.lost}/${bk.tickets}`;
      setText(this.el.scoreCrimson, `CRIMSON ${bk.attacker === 0 ? burn : down}`);
      setText(this.el.scoreCobalt, `${bk.attacker === 1 ? burn : down} COBALT`);
      const m = Math.floor(sc.t / 60);
      const s = Math.floor(sc.t % 60);
      setText(this.el.timer, `${m}:${String(s).padStart(2, "0")}`);
      const role = bk.selfTeam === bk.attacker ? "RAID" : bk.selfTeam === 1 - bk.attacker ? "DEFEND" : "BRICKTON";
      setText(
        this.el.zone,
        `${role} · BRICKTON ${Math.round(bk.burn * 100)}/${Math.round(bk.burnToWin * 100)}% ABLAZE · RAID LOSSES ${bk.lost}/${bk.tickets}`,
      );
      setClass(this.el.zone, bk.attacker === 0 ? "crimson" : "cobalt");
      return;
    }
    if (fleet) {
      // Fleet action: the bar tracks flagship health, not points.
      const pct = (f) => (f <= 0 ? "SUNK" : `${Math.max(1, Math.round(f * 100))}%`);
      setText(this.el.scoreCrimson, `CRIMSON ⚓${pct(fleet.c)}`);
      setText(this.el.scoreCobalt, `⚓${pct(fleet.b)} COBALT`);
    } else {
      setText(this.el.scoreCrimson, `CRIMSON ${sc.c}`);
      setText(this.el.scoreCobalt, `${sc.b} COBALT`);
    }
    // Naval/land theaters: a points race over domination zones. The team
    // scores are already set above; show the round clock and the ZONES chip.
    if (sc.md === "naval" || sc.md === "land") {
      const nm = Math.floor(sc.t / 60);
      const ns = Math.floor(sc.t % 60);
      setText(this.el.timer, `${nm}:${String(ns).padStart(2, "0")}`);
      // Domination: how many capture zones each side holds right now.
      const z = this.el.zone;
      const zs = sc.zs ?? (sc.z != null ? [sc.z] : []);
      this.navalZoneHolders = zs; // remembered for the minimap's zone rings
      let cz = 0, bz = 0;
      for (const h of zs) {
        if (h === 0) cz++;
        else if (h === 1) bz++;
      }
      setHTML(z, `ZONES <span class="crimson">${cz}</span>&ndash;<span class="cobalt">${bz}</span>`);
      setClass(z, cz > bz ? "crimson" : bz > cz ? "cobalt" : "");
      return;
    }
    const m = Math.floor(sc.t / 60);
    const s = Math.floor(sc.t % 60);
    setText(this.el.timer, `${m}:${String(s).padStart(2, "0")}`);
    const z = this.el.zone;
    const ring = fleet ? "REPAIR RING" : "SKY RING";
    const holder = ["crimson", "cobalt", "contested"][sc.z] ?? "";
    setText(z, `${ring}: ${holder ? holder.toUpperCase() : "OPEN"}`);
    setClass(z, holder);
  }

  /**
   * Naval conning panel: the loaded shell (AP/HE), the damage-control state,
   * and any fire/flooding. `s` is null when not conning a warship (panel
   * hidden), else {ap, dcActive, dcReady, onfire, flooding}.
   */
  setNaval(s) {
    if (!this.el.navalPanel) return;
    if (!s) {
      if (!this.el.navalPanel.classList.contains("hidden")) {
        this.el.navalPanel.classList.add("hidden");
        // Leaving the conn: the BOMB button stops being a torpedo lamp.
        document.getElementById("tc-bomb")?.classList.remove("cooling");
      }
      return;
    }
    this.el.navalPanel.classList.remove("hidden");
    // On a phone the panel is a slim left-rail column, so the chips speak in
    // short code (SMK/REP/BST) — every saved character is horizon glass.
    const touch = document.body.classList.contains("touch");
    // Loaded main-battery shell: HE / AP / SS (star shell) — and a named
    // round's bore after the nature on desktop (AP 18.1″), never on the
    // phone's slim rail.
    const ammo = s.ammo | 0;
    const nature = ["HE", "AP", "SS"][ammo] ?? "HE";
    setText(this.el.navalShell, s.bore && ammo !== 2 && !touch ? `${nature} ${s.bore}` : nature);
    setClass(this.el.navalShell, ["naval-he", "naval-ap", "naval-ss"][ammo] ?? "naval-he");
    // Torpedo readout, shown only on hulls with tubes. On touch the chip row
    // is hidden (CSS) and the readiness rides the BOMB button that actually
    // fires the spread — dimmed while the tubes reload.
    const tp = this.el.navalTorp;
    if (tp) {
      if (s.torp === undefined) {
        tp.classList.add("hidden");
      } else {
        tp.classList.remove("hidden");
        setText(tp, s.torp ? "TORP ⊕" : "TORP …");
        setClass(tp, s.torp ? "torp-ready" : "torp-cooldown");
      }
    }
    if (touch) document.getElementById("tc-bomb")?.classList.toggle("cooling", s.torp === false);
    // Smoke-screen readiness (laid with C). Hidden while the consumable is
    // disabled (s.smoke === null — see naval.smokeEnabled).
    const sm = this.el.navalSmoke;
    if (sm) {
      if (s.smoke === null) {
        sm.classList.add("hidden");
      } else {
        sm.classList.remove("hidden");
        setText(sm, (touch ? "SMK " : "SMOKE ") + (s.smoke ? "⊕" : "…"));
        setClass(sm, s.smoke ? "torp-ready" : "torp-cooldown");
      }
    }
    // Repair party (T): active while healing, else ready/cooldown.
    const rp = this.el.navalRepair;
    if (rp) {
      setText(rp, (touch ? "REP " : "REPAIR ") + (s.repairActive ? "✚" : s.repairReady ? "⊕" : "…"));
      setClass(rp, s.repairActive ? "dc-active" : s.repairReady ? "torp-ready" : "torp-cooldown");
    }
    // Engine boost (G): active while spooled up, else ready/cooldown.
    const bo = this.el.navalBoost;
    if (bo) {
      setText(bo, (touch ? "BST " : "BOOST ") + (s.boostActive ? "✦" : s.boostReady ? "⊕" : "…"));
      setClass(bo, s.boostActive ? "dc-active" : s.boostReady ? "torp-ready" : "torp-cooldown");
    }
    const dc = this.el.navalDC;
    if (s.dcActive) {
      setText(dc, touch ? "DC ✚" : "DC ACTIVE");
      setClass(dc, "dc-active");
    } else if (s.dcReady) {
      setText(dc, touch ? "DC ⊕" : "DC READY");
      setClass(dc, "dc-ready");
    } else {
      setText(dc, "DC …");
      setClass(dc, "dc-cooldown");
    }
    let dot = "";
    if (s.onfire) dot += "🔥 FIRE ";
    if (s.flooding) dot += "🌊 FLOODING";
    setText(this.el.navalDot, dot);
  }

  /**
   * Land fighting-compartment panel: the same chip row as the naval conning
   * panel (same wire, same buttons) wearing land labels. `s` is null when not
   * crewing a land vehicle, else {ammo, hasAmmoToggle, round, atgm, coax, smoke,
   * repairActive, repairReady, arty, boostActive, boostReady, onfire}.
   * Chip → key mapping: shell=X, dc=R (field repair), smoke=C,
   * repair=T (artillery), boost=G.
   */
  setLand(s) {
    if (!this.el.navalPanel) return;
    if (!s) {
      if (!this.el.navalPanel.classList.contains("hidden")) {
        this.el.navalPanel.classList.add("hidden");
        // Dismounting: the BOMB button stops being an ATGM lamp.
        document.getElementById("tc-bomb")?.classList.remove("cooling");
      }
      return;
    }
    this.el.navalPanel.classList.remove("hidden");
    // Same left-rail column deal as setNaval: short code on touch.
    const touch = document.body.classList.contains("touch");
    // Loaded nature (X). A single-nature gun greys the chip and names what
    // it fires instead (s.round: HEAT, 25MM...), never a nature it can't load.
    const ammo = s.hasAmmoToggle || !s.round ? (s.ammo === 1 ? "AP" : "HE") : s.round;
    setText(this.el.navalShell, ammo);
    setClass(this.el.navalShell, s.hasAmmoToggle ? (s.ammo === 1 ? "naval-ap" : "naval-he") : "naval-he chip-off");
    // ATGM readiness (B), only on a hull with a launcher. Touch folds it into
    // the BOMB button, same as the torpedo tubes at sea.
    const tp = this.el.navalTorp;
    if (tp) {
      if (s.atgm === undefined && s.coax) {
        // No launcher, but a coax in the mantlet: the chip says what B
        // does on this hull. It never cools — a belt, not a reload. B is
        // the pilot's rebindable bomb key, so the chip names the live one.
        tp.classList.remove("hidden");
        setText(tp, touch ? "COAX" : `COAX (${codeHint(keymap.keysFor("bomb")[0])})`);
        setClass(tp, "torp-ready");
      } else if (s.atgm === undefined) {
        tp.classList.add("hidden");
      } else {
        tp.classList.remove("hidden");
        setText(tp, s.atgm ? "ATGM ⊕" : "ATGM …");
        setClass(tp, s.atgm ? "torp-ready" : "torp-cooldown");
      }
    }
    if (touch) document.getElementById("tc-bomb")?.classList.toggle("cooling", s.atgm === false);
    // Smoke launchers (C).
    const sm = this.el.navalSmoke;
    if (sm) {
      sm.classList.remove("hidden");
      setText(sm, (touch ? "SMK " : "SMOKE ") + (s.smoke ? "⊕" : "…"));
      setClass(sm, s.smoke ? "torp-ready" : "torp-cooldown");
    }
    // Artillery fire mission (T) rides the naval repair chip slot.
    const rp = this.el.navalRepair;
    if (rp) {
      setText(rp, s.arty ? "ARTY ⊕" : "ARTY …");
      setClass(rp, s.arty ? "torp-ready" : "torp-cooldown");
    }
    // Overdrive (G).
    const bo = this.el.navalBoost;
    if (bo) {
      setText(bo, s.boostActive ? "OVRD ✦" : s.boostReady ? "OVRD ⊕" : "OVRD …");
      setClass(bo, s.boostActive ? "dc-active" : s.boostReady ? "torp-ready" : "torp-cooldown");
    }
    // Field repair (R) rides the DC chip slot.
    const dc = this.el.navalDC;
    if (s.repairActive) {
      setText(dc, touch ? "REP ✚" : "REPAIRING");
      setClass(dc, "dc-active");
    } else if (s.repairReady) {
      setText(dc, touch ? "REP ⊕" : "REPAIR RDY");
      setClass(dc, "dc-ready");
    } else {
      setText(dc, touch ? "REP …" : "REPAIR …");
      setClass(dc, "dc-cooldown");
    }
    setText(this.el.navalDot, s.onfire ? "🔥 FIRE" : "");
  }

  setFlight(speedMS, alt, throttle, hp, maxHp) {
    setText(this.el.spd, Math.round(speedMS * 3.6));
    setText(this.el.alt, Math.max(0, Math.round(alt)));
    setStyle(this.el.thr, "width", `${Math.round(throttle * 100)}%`);
    const frac = Math.max(0, hp / maxHp);
    setStyle(this.el.hp, "width", `${(frac * 100).toFixed(1)}%`);
    setClass(this.el.hp, "fill" + (frac < 0.3 ? " critical" : frac < 0.6 ? " hurt" : ""));
  }

  /**
   * Update the localized-damage panel and pilot-wound vignette. d is
   * {engine,wingL,wingR,fuel,pilot} (0..1 each); flying gates visibility (it
   * clears while dead, manning a gun, or driving). The two wings collapse to
   * the worse of the pair on one bar.
   */
  setDamage(d, flying) {
    const pristine =
      !d || ((d.engine ?? 1) >= 1 && (d.wingL ?? 1) >= 1 && (d.wingR ?? 1) >= 1 &&
        (d.fuel ?? 1) >= 1 && (d.pilot ?? 1) >= 1);
    this.subsys.classList.toggle("hidden", !flying || pristine);
    setStyle(this.pveil, "opacity", flying && d ? ((1 - clamp01(d.pilot ?? 1)) * 0.6).toFixed(3) : "0");
    if (!flying || pristine) return;
    const set = (fill, v) => {
      const f = clamp01(v);
      setStyle(fill, "width", `${(f * 100).toFixed(0)}%`);
      setClass(fill, "subsys-fill" + (f < 0.25 ? " crit" : f < 0.6 ? " hurt" : ""));
    };
    set(this._subBars.engine, d.engine ?? 1);
    set(this._subBars.wing, Math.min(d.wingL ?? 1, d.wingR ?? 1));
    set(this._subBars.fuel, d.fuel ?? 1);
    set(this._subBars.pilot, d.pilot ?? 1);
  }

  /** Show ordnance remaining (hidden for types that carry none).
   *  kind: "bomb" | "torp" | "rocket" picks the gauge label. */
  setBombs(visible, count, kind = "bomb") {
    this.el.bombsGauge.classList.toggle("hidden", !visible);
    if (!visible) return;
    setText(this.el.bombs, count);
    if (kind !== this._ordGauge) {
      this._ordGauge = kind;
      const label = kind === "torp" ? "TRP" : kind === "rocket" ? "RKT" : "BMB";
      const icon = kind === "torp" ? "🐟" : kind === "rocket" ? "🚀" : "💣";
      this.el.bombsGauge.querySelector("label").textContent = label;
      this.el.bombsGauge.querySelector("small").textContent = icon;
    }
  }

  /**
   * G-meter + physiological veils. Call every frame with the load factor
   * (g), the frame dt, and whether the player is actually flying (false
   * while dead, manning a gun, or spectating clears everything). Returns
   * nothing; the veils and readout update in place.
   */
  gForce(nz, dt, flying) {
    this.el.gGauge.classList.toggle("hidden", !flying);
    // Lightly smooth the digit so it doesn't strobe at 60 Hz.
    this._gShown += ((flying ? nz : 1) - this._gShown) * 0.25;
    setText(this.el.gload, this._gShown.toFixed(1));
    setClass(this.el.gload, !flying ? "" : nz > 6 || nz < -2 ? "g-crit" : nz > 4 || nz < -1 ? "g-warn" : "");

    // Targets from the instantaneous load; build slowly, recover faster, the
    // way grey-out lags onset and clears once the pull eases. Calibrated for a
    // no-G-suit warbird: grey-out creeps in around 3.5 g and the tunnel closes
    // to a full black-out by ~6.5 g — the band a hard sustained turn or a
    // pull-up actually lives in now that the instructor limiter holds a
    // committed haul around 5–6 g. (The old 4.5→8 band was set back when
    // unlimited yanks were common and left the veil all but invisible in a
    // normal high-G fight.)
    const posT = flying ? clamp01((nz - 3.5) / (6.5 - 3.5)) : 0;
    const negT = flying ? clamp01((-1.5 - nz) / (-1.5 - -3.5)) : 0;
    this._gGrey = approach(this._gGrey, posT, posT > this._gGrey ? 0.6 : 1.1, dt);
    this._gRed = approach(this._gRed, negT, negT > this._gRed ? 0.8 : 1.5, dt);

    // The veil is a full-screen gradient: every new value is a full-screen
    // re-raster, so it changes only when its rounded stop moves, and at zero
    // grey (every frame of level flight) it isn't drawn at all. The decay
    // snaps to nothing below half a percent rather than easing toward zero
    // forever, one invisible repaint a frame.
    if (this._gGrey < 0.005) this._gGrey = 0;
    if (this._gRed < 0.005) this._gRed = 0;
    const grey = this._gGrey;
    const close = 78 - 72 * grey; // transparent radius shrinks as vision tunnels
    setStyle(this.gveil, "opacity", Math.min(1, grey * 1.5).toFixed(2));
    setStyle(
      this.gveil,
      "background",
      grey > 0
        ? `radial-gradient(circle at 50% 50%, rgba(0,0,0,0) ${Math.max(2, close).toFixed(0)}%,` +
            ` rgba(0,0,0,0.94) ${Math.max(22, close + 24).toFixed(0)}%)`
        : "none",
    );
    setStyle(this.rveil, "opacity", (this._gRed * 0.82).toFixed(2));
  }

  /**
   * Pre-stall feedback: show the STALL flag and shake the view when the wing
   * passes `frac` (= |AoA| / stall AoA) ~0.9. Returns true while buffeting so
   * the caller can throttle the airframe-rumble sound.
   */
  setStall(frac, flying) {
    const stalling = flying && frac >= 0.9;
    this.el.stall.classList.toggle("hidden", !stalling);
    if (stalling) {
      const amp = Math.min(7, 1 + (frac - 0.9) * 26);
      this.el.game.style.transform =
        `translate(${((Math.random() - 0.5) * amp).toFixed(1)}px, ${((Math.random() - 0.5) * amp).toFixed(1)}px)`;
    } else if (this._stalling) {
      this.el.game.style.transform = "";
    }
    this._stalling = stalling;
    return stalling;
  }

  /**
   * Push one line into a feed stream. `kind` is "event" (kills and world
   * events — the default, into the top-right #killfeed) or "chat" (team
   * comms, into the bottom-left #chatfeed above the composer). The lanes are
   * separate on purpose: the combat log scrolls constantly, and a human
   * saying something was drowning in it — now when the left lane moves, it's
   * a voice. Each stream is capped on its own. Newest prepends to the DOM
   * head (#chatfeed renders column-reverse, so chat reads newest-at-bottom
   * like a chat). Returns the line element for the caller to schedule fades.
   */
  pushFeed(html, kind = "event") {
    const line = document.createElement("div");
    line.className = kind === "chat" ? "feed-line chat" : "feed-line";
    line.innerHTML = html;
    const host = kind === "chat" ? this.el.chatfeed : this.el.killfeed;
    host.prepend(line);
    // Phones have far less room, so keep each stream short there. Checked at
    // call time because the touch body class is set after the HUD is built.
    const touch = document.body.classList.contains("touch");
    const cap = kind === "chat" ? (touch ? 3 : 5) : touch ? 3 : 6;
    const same = host.querySelectorAll(".feed-line");
    for (let i = cap; i < same.length; i++) same[i].remove();
    return line;
  }

  feed(html) {
    const line = this.pushFeed(html, "event");
    // World/combat events age out on their own.
    setTimeout(() => line.classList.add("fade"), 6000);
    setTimeout(() => line.remove(), 6700);
  }

  // Each name in a line wears its pilot's unit insignia in front of it (the
  // *Ins arguments; "" for none — flak batteries and the like have no badge).
  feedKill(killerName, killerTeam, victimName, victimTeam, badge = "", killerIns = "", victimIns = "") {
    const k =
      killerName !== null
        ? `<span class="${TEAM_CLASS[killerTeam]}">${insigniaHTML(killerIns)}${esc(killerName)}</span> ✚ `
        : "";
    const b = badge ? ` <span class="feed-badge">${esc(badge)}</span>` : "";
    this.feed(
      `${k}<span class="${TEAM_CLASS[victimTeam]}">${insigniaHTML(victimIns)}${esc(victimName)}</span> ✝${b}`,
    );
  }

  /** Strafing a bailed pilot under his chute (roadmap #1): a red-branded
   *  DISHONORABLE line, no kill cross. */
  feedDishonor(shooterName, shooterTeam, victimName, victimTeam, shooterIns = "", victimIns = "") {
    const k = `<span class="${TEAM_CLASS[shooterTeam]}">${insigniaHTML(shooterIns)}${esc(shooterName)}</span> ☠ `;
    this.feed(
      `${k}<span class="${TEAM_CLASS[victimTeam]}">${insigniaHTML(victimIns)}${esc(victimName)}</span>` +
        ` <span class="feed-badge dishonor">DISHONORABLE</span>`,
    );
  }

  /** Drop a chat line into the shared feed. Comms linger, then fade to a ghost
   *  but stay in the DOM (capped in pushFeed) so opening the composer can
   *  resurface the recent thread. The 💬 tag + accent set it apart from kills. */
  chat(name, team, text, ins = "") {
    const line = this.pushFeed(
      `<span class="chat-tag" aria-hidden="true">💬</span>` +
        `<span class="${TEAM_CLASS[team]}">${insigniaHTML(ins)}${esc(name)}:</span> ${esc(text)}`,
      "chat",
    );
    const touch = document.body.classList.contains("touch");
    setTimeout(() => line.classList.add("fade"), touch ? 6000 : 9000);
  }

  /** Toggle the chat composer; focus moves into/out of the input. The body flag
   *  resurfaces the faded chat lines in the shared feed while you're typing. */
  setChatOpen(open) {
    this.el.chat.classList.toggle("open", open);
    document.body.classList.toggle("chatting", open);
    this.el.chatEntry.classList.toggle("hidden", !open);
    if (open) {
      this.el.chatInput.value = "";
      this.el.chatInput.focus();
    } else {
      this.el.chatInput.blur();
    }
  }

  message(text, ms = 3000, color = "") {
    this.el.message.textContent = text;
    this.el.message.style.color = color;
    clearTimeout(this._msgTimer);
    this._msgTimer = setTimeout(() => (this.el.message.textContent = ""), ms);
  }

  // Persistent "now playing" chip for the director stream: names the current
  // procedural soundtrack theme with an animated equalizer. Built in JS so it
  // needs no index.html change; created on first call (director mode only).
  // Cinematic kill-confirm banner (roadmap #4): killer-plane silhouette +
  // "SPARROWHAWK ✈ Baron von Brick". Built in JS like the now-playing chip so
  // it needs no index.html change; re-stamping while visible refreshes the
  // text and restarts the timer.
  killBanner(planeIdx, planeName, victimName, ms = 1400, victimIns = "") {
    if (!this._killBanner) {
      const el = document.createElement("div");
      el.id = "kill-banner";
      el.innerHTML = '<canvas width="48" height="20"></canvas><span class="kb-text"></span>';
      document.body.appendChild(el);
      this._killBanner = el;
      this._killBannerText = el.querySelector(".kb-text");
      this._killBannerCanvas = el.querySelector("canvas");
    }
    drawPlaneSilhouette(this._killBannerCanvas.getContext("2d"), planeIdx);
    this._killBannerText.innerHTML =
      `${esc((planeName || "").toUpperCase())} ✈ ${insigniaHTML(victimIns)}${esc(victimName)}`;
    this._killBanner.classList.remove("show");
    void this._killBanner.offsetWidth; // reflow so the slide-in restarts
    this._killBanner.classList.add("show");
    clearTimeout(this._killBannerTimer);
    this._killBannerTimer = setTimeout(() => this._killBanner.classList.remove("show"), ms);
  }

  // Persistent passenger chip for a bomber gunner: names whose bomber you're
  // riding and how to leave, so the ride never reads as a control-less
  // spectator. Built in JS like the now-playing chip (no index.html change);
  // pass null to hide. Cheap to call every frame — it only touches the DOM when
  // the text actually changes.
  setTurretChip(text) {
    if (text == null) {
      if (this._turretChip) this._turretChip.classList.add("hidden");
      this._turretChipText = null;
      return;
    }
    if (!this._turretChip) {
      const el = document.createElement("div");
      el.id = "turret-chip";
      el.className = "hidden";
      el.innerHTML = '<span class="tc-icon">▣</span><span class="tc-text"></span>';
      document.body.appendChild(el);
      this._turretChip = el;
      this._turretChipLabel = el.querySelector(".tc-text");
    }
    this._turretChip.classList.remove("hidden");
    if (text !== this._turretChipText) {
      this._turretChipText = text;
      this._turretChipLabel.textContent = text;
    }
  }

  nowPlaying(name) {
    if (!this._nowPlaying) {
      const el = document.createElement("div");
      el.id = "now-playing";
      el.innerHTML =
        '<div class="np-eq"><span></span><span></span><span></span></div>' +
        '<div class="np-text"><span class="np-label">NOW PLAYING</span>' +
        '<span class="np-name"></span></div>';
      document.body.appendChild(el);
      this._nowPlaying = el;
      this._nowPlayingName = el.querySelector(".np-name");
    }
    this._nowPlayingName.textContent = name;
    // Re-trigger the flash animation on each theme change.
    this._nowPlaying.classList.remove("np-bump", "np-fade");
    void this._nowPlaying.offsetWidth; // reflow so the animation restarts
    this._nowPlaying.classList.add("np-bump");
    // A ticker, not furniture: announce the theme change, then get out of
    // the way (the soundtrack is on by default now, so everyone sees this).
    clearTimeout(this._npTimer);
    this._npTimer = setTimeout(() => this._nowPlaying.classList.add("np-fade"), 6000);
  }

  // Persistent killcam badge: shown for the whole death replay so it reads as
  // a killcam (the live killer-cam that follows looks similar) and keeps the
  // killer's name on screen the entire time. Built in JS like the now-playing
  // chip, so it needs no index.html change. Pass null to hide it. Crash
  // replays override `label` (e.g. "↻ CRASH REPLAY") and blank `by`/`name`
  // since there is no killer to credit.
  setKillcamTag(name, coachLine = "", label = "↻ KILLCAM", by = "shot down by", tipLine = "", ins = "") {
    if (!this._killcamTag) {
      const el = document.createElement("div");
      el.id = "killcam-tag";
      el.className = "hidden";
      const touch = document.body.classList.contains("touch");
      el.innerHTML =
        '<div class="kc-rule"></div>' +
        '<div class="kc-body"><span class="kc-label">↻ KILLCAM</span>' +
        '<span class="kc-by">shot down by</span>' +
        '<span class="kc-name"></span>' +
        '<span class="kc-coach hidden"></span>' +
        '<span class="kc-tip hidden"></span>' +
        `<span class="kc-skip">${touch ? "tap" : "click"} anywhere to skip</span></div>` +
        '<div class="kc-rule"></div>';
      document.body.appendChild(el);
      this._killcamTag = el;
      this._killcamLabel = el.querySelector(".kc-label");
      this._killcamBy = el.querySelector(".kc-by");
      this._killcamName = el.querySelector(".kc-name");
      this._killcamCoach = el.querySelector(".kc-coach");
      this._killcamTip = el.querySelector(".kc-tip");
    }
    if (name == null) {
      this._killcamTag.classList.add("hidden");
      return;
    }
    this._killcamLabel.textContent = label;
    this._killcamBy.textContent = by;
    this._killcamBy.classList.toggle("hidden", !by);
    // The killer's unit insignia leads the name: the badge you'll be hunting.
    this._killcamName.innerHTML = name ? `${insigniaHTML(ins, "ins kc-ins")}${esc(name)}` : "";
    this._killcamName.classList.toggle("hidden", !name);
    this._killcamCoach.textContent = coachLine;
    this._killcamCoach.classList.toggle("hidden", !coachLine);
    // "Did you know?" field-manual line under the coach: the replay is the one
    // moment the player is watching and not flying, so it can carry one fact.
    this._killcamTip.textContent = tipLine ? `★ DID YOU KNOW — ${tipLine}` : "";
    this._killcamTip.classList.toggle("hidden", !tipLine);
    this._killcamTag.classList.remove("hidden");
  }

  // Enemy markers: off-screen bandits hug the screen edge with a team-coloured
  // ▲ pointing toward them; hull-theater targets that ARE on screen instead get
  // a ▽ caret hovering over the hull (`on: true`) — a spotted tank is a 3-px
  // silhouette without one. A fixed pool of divs is reused every frame (no
  // per-frame allocation / GC). `list` is [{ ndcX, ndcY, dist, team, on?,
  // hull? }], already clamped to the edge box and sorted by distance; pass []
  // to clear. Air markers show on touch only; hull markers everywhere (CSS).
  setEdgeMarkers(list) {
    const POOL = 6;
    if (!this._edgeMarkers) {
      this._edgeMarkers = [];
      for (let i = 0; i < POOL; i++) {
        const el = document.createElement("div");
        el.className = "edge-marker hidden";
        el.textContent = "▲";
        this.el.hud.appendChild(el);
        this._edgeMarkers.push(el);
      }
    }
    const W = viewport.w, H = viewport.h, inset = 24;
    for (let i = 0; i < POOL; i++) {
      const el = this._edgeMarkers[i];
      const m = list[i];
      if (!m) {
        if (!el.classList.contains("hidden")) el.classList.add("hidden");
        continue;
      }
      let px = (m.ndcX * 0.5 + 0.5) * W;
      let py = (-m.ndcY * 0.5 + 0.5) * H;
      px = Math.max(inset, Math.min(W - inset, px));
      py = Math.max(inset, Math.min(H - inset, py));
      // Placed by transform alone (the CSS pins it at 0,0), so a marker
      // tracking a bandit never asks the page for a layout.
      const at = `translate(${px.toFixed(1)}px, ${py.toFixed(1)}px) `;
      if (m.on) {
        // On-screen: the caret floats just above the hull, no rotation.
        setText(el, "▽");
        setStyle(el, "transform", at + "translate(-50%, -135%)");
      } else {
        // Rotate the ▲ (points up at 0°) so it aims outward toward the bandit.
        setText(el, "▲");
        const rot = (Math.atan2(m.ndcX, m.ndcY) * 180) / Math.PI;
        setStyle(el, "transform", at + `translate(-50%, -50%) rotate(${rot.toFixed(1)}deg)`);
      }
      setClass(el, "edge-marker " + (m.hull ? "hull " : "") + (m.team === 1 ? "cobalt" : "crimson"));
      // 1.0 at <=600 m, fading to 0.35 by 1500 m.
      const t = Math.min(1, Math.max(0, (m.dist - 600) / 900));
      setStyle(el, "opacity", (1 - t * 0.65).toFixed(2));
    }
  }

  setAutopilot(on) {
    this.el.autopilot.classList.toggle("hidden", !on);
  }

  /** Elevator-trim readout. `on` greys the gauge when auto-trim is disabled;
   *  `value` is the held trim in -1..1 (+ = nose-up), riding the tick up/down. */
  setTrim(value, on) {
    this.el.trimGauge.classList.toggle("off", !on);
    const v = value < -1 ? -1 : value > 1 ? 1 : value;
    // +1 nose-up -> tick to the right (92%); -1 nose-down -> left (8%).
    // Left stays (the tick eases along it), but only moves when the trim does.
    setStyle(this.el.trimTick, "left", `${(50 + v * 42).toFixed(1)}%`);
  }

  hitmarker() {
    this.el.crosshair.classList.add("hitmarker");
    clearTimeout(this._hitTimer);
    this._hitTimer = setTimeout(() => this.el.crosshair.classList.remove("hitmarker"), 120);
  }

  damageFlash() {
    this.flash.style.transition = "none";
    this.flash.style.opacity = "1";
    requestAnimationFrame(() => {
      this.flash.style.transition = "opacity 0.5s";
      this.flash.style.opacity = "0";
    });
  }

  /** Directional damage flash (roadmap #56): light the screen edge the fire
   *  came from. bearing is 0 dead ahead, +right, ±π astern, -left. */
  hitFrom(bearing) {
    const a = bearing;
    let q; // 0 front, 1 right, 2 back, 3 left
    if (a >= -Math.PI / 4 && a < Math.PI / 4) q = 0;
    else if (a >= Math.PI / 4 && a < (3 * Math.PI) / 4) q = 1;
    else if (a >= (-3 * Math.PI) / 4 && a < -Math.PI / 4) q = 3;
    else q = 2;
    const now = Date.now();
    if (now - this._dmgArcAt[q] < 100) return; // one flash per quarter per 100 ms
    this._dmgArcAt[q] = now;
    const el = this.dmgArcs[q];
    el.style.transition = "none";
    el.style.opacity = "1";
    requestAnimationFrame(() => {
      el.style.transition = "opacity 0.35s";
      el.style.opacity = "0";
    });
  }

  /** Stall-margin ribbon (roadmap #56): how much turn is left before the buffet
   *  — green under 0.6, gold to 0.9, red at the buffet threshold. */
  setStallRibbon(frac, flying) {
    this.el.stallGauge.classList.toggle("hidden", !flying);
    if (!flying) return;
    const f = Math.min(1, Math.max(0, frac));
    const el = this.el.stallRibbon;
    setStyle(el, "height", (f * 100).toFixed(0) + "%");
    setStyle(el, "background", f >= 0.9 ? "#ff5a4a" : f >= 0.6 ? "#f4c542" : "#5fe07a");
  }

  /** Bomb-release confidence ring (roadmap #56): a dispersion circle around the
   *  bombsight pip, gold when it's a killing solution. */
  setBombRing(visible, ndcX, ndcY, pxRadius, gold) {
    const el = this.el.bombRing;
    el.classList.toggle("hidden", !visible);
    if (!visible) return;
    placeAtNDC(el, ndcX, ndcY);
    const d = Math.max(8, pxRadius * 2).toFixed(0) + "px";
    setStyle(el, "width", d);
    setStyle(el, "height", d);
    setStyle(el, "borderColor", gold ? "#f4c542" : "#9aa3ad");
  }

  /**
   * Show or hide the respawn screen, which is simply the join screen shown
   * again — same masthead, unit picker, deck choice and daily missions. The
   * killcam replay (not this screen) names who got you, so there's no death
   * banner. The `respawning` class is an internal marker only: it lets a
   * per-frame setRespawn(false) tell the respawn screen apart from the
   * first-join screen (which show() owns) so it never hides the latter early.
   */
  setRespawn(visible) {
    const js = this.el.joinScreen;
    if (!visible) {
      if (js.classList.contains("respawning")) {
        js.classList.remove("respawning");
        js.classList.add("hidden");
        document.body.classList.remove("spawn-open");
      }
      return;
    }
    js.classList.add("respawning");
    js.classList.remove("hidden");
    // Mirror card visibility onto the body so the flight HUD behind it can be
    // hidden (see the body.spawn-open rules) — the killcam has already played.
    document.body.classList.add("spawn-open");
  }

  /** What the missions BUY — one line of incentive under the board (user
   *  feedback #11: goals with no visible prize read as busywork). Set once
   *  from config by main.js; renders on the next setDaily rebuild. */
  setDailyReward(text) {
    this._dailyReward = text ?? "";
    this._dailyKey = null; // force the next setDaily to rebuild with the line
  }

  /**
   * Paint the spawn-screen daily-mission panel: one row per challenge with a
   * progress bar. Rebuilds only when the set changes (each frame's setRespawn
   * may call through), then patches fill widths and the done state in place.
   */
  setDaily(list) {
    const root = this.el.respawnDaily;
    if (!list?.length) {
      root.innerHTML = "";
      this._dailyKey = null;
      return;
    }
    const layoutKey = list.map((c) => c.id).join(",");
    if (this._dailyKey !== layoutKey) {
      this._dailyKey = layoutKey;
      root.innerHTML = `<div class="daily-head">TODAY'S MISSIONS</div>`;
      this._dailyRows = new Map();
      for (const c of list) {
        const row = document.createElement("div");
        row.className = "daily-row";
        row.innerHTML =
          `<div class="daily-desc"><span class="daily-text"></span><span class="daily-have"></span></div>` +
          `<div class="daily-bar"><div class="daily-fill"></div></div>`;
        row.querySelector(".daily-text").textContent = c.desc ?? "";
        root.appendChild(row);
        this._dailyRows.set(c.id, row);
      }
      if (this._dailyReward) {
        const rw = document.createElement("div");
        rw.className = "daily-reward";
        rw.textContent = this._dailyReward;
        root.appendChild(rw);
      }
    }
    for (const c of list) {
      const row = this._dailyRows.get(c.id);
      if (!row) continue;
      const have = Math.min(c.have ?? 0, c.goal);
      const done = have >= c.goal;
      row.classList.toggle("done", done);
      row.querySelector(".daily-have").textContent = `${have}/${c.goal}`;
      row.querySelector(".daily-fill").style.width = `${c.goal ? (have / c.goal) * 100 : 0}%`;
    }
  }

  /** Build the spawn-site picker once per join: launch from the base or be
   * catapulted off the carrier's bow. Availability painted while dead. */
  buildDeckSelect(onPick) {
    const root = this.el.respawnDeck;
    root.innerHTML = "";
    this._deckChips = new Map();
    const label = document.createElement("div");
    label.className = "gs-label";
    label.textContent = "LAUNCH FROM";
    root.appendChild(label);
    for (const c of [
      { deck: false, label: "⛰ BASE" },
      { deck: true, label: "⚓ CARRIER DECK" },
    ]) {
      const btn = document.createElement("button");
      btn.className = "gun-chip";
      btn.textContent = c.label;
      btn.onclick = () => onPick(c.deck);
      root.appendChild(btn);
      this._deckChips.set(c.deck, btn);
    }
  }

  updateDeckSelect(pendingDeck, carrierAlive) {
    if (!this._deckChips) return;
    for (const [deck, btn] of this._deckChips) {
      btn.classList.toggle("picked", pendingDeck === deck);
      if (deck) {
        btn.disabled = !carrierAlive;
        btn.classList.toggle("destroyed", !carrierAlive);
      }
    }
  }

  /** Show or hide the "LAUNCH FROM" deck picker — it only applies when the
   *  selected unit is an aircraft, so picking a gun/vehicle hides it. */
  setDeckVisible(visible) {
    this.el.respawnDeck.classList.toggle("hidden", !visible);
  }

  /** Position the mouse-aim cursor (NDC coords) or hide it. */
  setCursor(visible, ndcX, ndcY) {
    const el = this.el.cursor;
    el.classList.toggle("hidden", !visible);
    if (visible) placeAtNDC(el, ndcX, ndcY);
  }

  /** The raw stick's neutral ring at screen centre: shown while the mouse
   *  flies as a stick, so the reticle's offset from it reads as deflection. */
  setStickCentre(visible) {
    this.el.stickCentre?.classList.toggle("hidden", !visible);
  }

  /** Position the gun reticle (NDC coords); pass (0, 0) to re-center it. */
  setCrosshair(ndcX, ndcY) {
    placeAtNDC(this.el.crosshair, ndcX, ndcY);
  }

  /** Position the gunnery lead pip (NDC coords) or hide it. */
  setLeadPip(visible, ndcX, ndcY) {
    const el = this.el.leadPip;
    el.classList.toggle("hidden", !visible);
    if (visible) placeAtNDC(el, ndcX, ndcY);
  }

  /**
   * Position the level-bombing pipper (NDC coords) over the predicted bomb
   * impact point, or hide it. Only the level bombers carry a bombsight.
   */
  setBombsight(visible, ndcX, ndcY) {
    const el = this.el.bombsight;
    el.classList.toggle("hidden", !visible);
    if (visible) placeAtNDC(el, ndcX, ndcY);
  }

  /**
   * Jets-era seeker ring + ordnance chips. st is null (hide: not an era
   * server, not flying a missile plane, dead) or { mode, prog, msl, fl }
   * where mode is "search" (caged), "track" (winding up, prog 0..1) or
   * "lock" (launch-ready). The state is SERVER truth from the snapshot's
   * jets section, so this ring never promises a launch the server refuses.
   */
  setSeeker(st) {
    const el = this.seeker;
    if (!st) {
      if (this._seekerMode !== null) {
        el.classList.add("hidden");
        this._seekerMode = null;
      }
      return;
    }
    el.classList.remove("hidden");
    this.seekerRing.classList.toggle("hidden", st.ring === false);
    if (st.mode !== this._seekerMode || st.hint !== this._seekerHint) {
      this._seekerMode = st.mode;
      this._seekerHint = st.hint;
      el.classList.toggle("seeker-track", st.mode === "track");
      el.classList.toggle("seeker-lock", st.mode === "lock");
      // The label speaks the pilot's ACTUAL trigger: "R TO LAUNCH" was a lie
      // the moment the key was rebound, and it was keyboard copy shown to a
      // thumb on the touch deck — where the missile button "did nothing".
      this.seekerLabel.textContent =
        st.mode === "lock" ? `LOCK — ${st.hint ?? "FIRE"}` : st.mode === "track" ? "SEEKER TRACKING" : "";
    }
    // The winding ring: scale closes from loose to boresight as prog rises.
    const k = st.mode === "lock" ? 1 : 1.5 - 0.5 * (st.prog ?? 0);
    setStyle(this.seekerRing, "transform", `translate(-50%, -50%) scale(${k.toFixed(3)})`);
    const chips = `▲ ${st.msl ?? 0}  ✺ ${st.fl ?? 0}`;
    if (this.seekerChips.textContent !== chips) this.seekerChips.textContent = chips;
  }

  setDeviceHint(device) {
    if (!this.el.deviceHint) return;
    this.el.deviceHint.textContent = device
      ? `${device.kind === "hotas" ? "HOTAS" : "gamepad"} connected: ${device.name}`
      : "gamepads & HOTAS sticks supported — just plug in and move an axis";
  }

  /** Render aircraft selection cards on the join screen. */
  /**
   * Join-screen unit picker. A row of category tabs (AIRCRAFT first / default)
   * sits on top; selecting a tab swaps in that category's stat cards, built the
   * same chunky way as the aircraft cards. Picking a card chooses what you
   * spawn as.
   *
   * onPick({ gk, plane }) — gk is the wire "spawn as" code (0 fly, 1 land flak,
   * 2 naval mount, 3 big guns; 11+ drive ground-vehicle type gk-11). plane is
   * the aircraft index, meaningful only when gk === 0. The server seats you in
   * the first free friendly emplacement / vehicle of that kind at the first
   * spawn, falling back to flying if none is free.
   */
  buildUnitSelect(config, types, calibers, selectedPlane, onPick) {
    const calByName = new Map((calibers ?? []).map((c) => [c.name, c]));
    // Guns-per-second "punch" on the same scale the aircraft cards use.
    const punchOf = (calName) => {
      const c = calByName.get(calName) ?? { damage: 8, roundsPerSec: 10 };
      return c.damage * c.roundsPerSec;
    };

    // --- aircraft cards (same stat maths as before) ---
    // The ad circus (adCraft, adfleet.go) never gets a card: bot-only craft,
    // filtered AFTER the map so every card keeps its true type index.
    const speedMax = 220;
    const aircraftCards = types.map((t, i) => {
      // Torpedoes count as heavy ordnance on the card; rockets ride the same
      // bar (a pod of them lands between a bomb load and a torpedo).
      const bombCount =
        (t.bombs?.count ?? 0) + (t.torpedoes?.count ?? 0) * 5 + (t.rockets?.count ?? 0) * 0.7;
      return {
        title: t.name,
        role: t.role ?? "",
        stats: [
          ["AGIL", (t.cmElevator / 0.5) * 100],
          ["SPD", ((t.maxThrustN / t.massKg) * 33000) / speedMax / 10],
          ["HULL", t.hpMult * 62],
          // The whole battery's weight of fire (battery.js), full bar at
          // 150 a second: a Mustang's six fifties fill two thirds of it, an
          // Fw 190's four cannon nearly all, a Ju 88's lone MG a sliver.
          ["GUNS", (batteryDps(t, calByName) / 150) * 100],
          ["BOMB", bombCount * 8],
        ],
        need: t.unlockKills ?? 0, // career kills before this type unlocks (0 = open)
        adCraft: !!t.adCraft,
        pick: { gk: 0, plane: i },
        preview: { plane: i }, // the hangar bay's turntable (hangar_preview.js)
      };
    })
      .filter((c) => !c.adCraft)
      // The hangar walks everything you can fly now before the airframes
      // you're still earning, and those in the order a career reaches them
      // (the Mk XVI at 5 kills, the 262 at 10, the cannon Corsair at 15, the
      // F-104 at 25; stable: roster order within each), so a carousel of
      // nineteen doesn't park a padlock in the middle of the props. Cards
      // are no longer one-per-index — the ad circus sits between the original
      // roster and the rest of the hangar — so lookups go by pick.plane.
      .sort((a, b) => (a.need > 0) - (b.need > 0) || (a.need > 0 ? a.need - b.need : 0));
    const aircraftCardFor = (idx) => aircraftCards.find((c) => c.pick.plane === idx);

    // --- a manned flak/naval AA emplacement (aaa/navy share a profile shape) ---
    const gunCard = (cfg, title, role, gk, gun) => ({
      title,
      role,
      stats: [
        ["HULL", (cfg.hp / 200) * 100],
        ["RANGE", (cfg.detectRangeM / 4000) * 100],
        ["TRACK", (cfg.trackRateRadPerSec / 2) * 100],
        ["GUNS", punchOf(cfg.caliber)],
      ],
      pick: { gk, plane: 0 },
      preview: { gun },
    });

    // --- battleship main battery: ballistic shells, not tracking flak ---
    const bb = config.battleship ?? {};
    const bigGunsCard = {
      title: "Battleship Turret",
      role: "Main battery — long-range ballistic shells",
      stats: [
        ["HULL", (bb.hp / 200) * 100],
        ["RANGE", (bb.maxRangeM / 4000) * 100],
        ["BLAST", (bb.shellBlastRadiusM / 60) * 100],
        ["PUNCH", (bb.shellDamage / 70) * 100],
      ],
      pick: { gk: 3, plane: 0 },
      preview: { gun: "bigGuns" },
    };

    // --- drivable ground vehicles, one card each ---
    // The AIR game's ground roster is the AA park — flak trucks and scout
    // cars whose whole story is what they can track and hit in the sky, so
    // that's what the card reads out.
    const vehCard = (t, i) => ({
      title: t.name,
      role: t.role ?? "",
      stats: [
        ["HULL", (t.hp / 200) * 100],
        ["SPD", (t.speedMS / 22) * 100],
        ["RANGE", ((t.aa?.detectRangeM ?? 0) / 4000) * 100],
        ["TRACK", ((t.aa?.trackRateRadPerSec ?? 0) / 2) * 100],
        ["GUNS", punchOf(t.aa?.caliber)],
      ],
      pick: { gk: 11 + i, plane: 0 },
      preview: { hull: i },
    });
    const vehTypes = config.groundVehicles?.types ?? [];

    // --- armor: the card a tanker actually shops by ---
    // A tank is not an AA truck, and the flak card was lying about it: the
    // Abrams' 600 mm glacis and 120 mm gun rendered as RANGE 0 / TRACK 0
    // and a GUNS bar that was really the "no AA mount here" fallback. Armor
    // is chosen on plate, punch and reach — the same shape as the warship
    // card, because the questions are the same ones.
    const armorCard = (t, i) => ({
      title: t.name,
      role: t.role ?? "",
      // Every scale leaves headroom above the best hull in the roster: a
      // picker where the Abrams pegs all five bars tells a driver nothing.
      stats: [
        ["HULL", (t.hp / 300) * 100],
        ["SPEED", (t.speedMS / 28) * 100],
        ["ARMOR", ((t.armorFrontMM ?? 0) / 700) * 100],
        // The main gun if it has one, else the autocannon's sustained punch on
        // a deliberately meaner scale — the Sd.Kfz. 7/1's quad 20 shreds trucks
        // and planes, but it is not a 106 mm rifle in a tank fight and the bar
        // shouldn't claim it is.
        ["GUN", t.mainGun ? ((t.mainGun.apDamage ?? 0) / 120) * 100 : (punchOf(t.aa?.caliber) / 200) * 100],
        ["REACH", ((t.mainGun?.rangeM ?? t.aa?.detectRangeM ?? 0) / 4500) * 100],
      ],
      pick: { gk: 11 + i, plane: 0 },
      preview: { hull: i },
    });

    // --- naval theater: a warship card with the stats that matter at sea ---
    const shipCard = (t, i) => ({
      title: t.name,
      role: t.role ?? "",
      stats: [
        // Out of 600: the real-scale hulls run 140 (Osa) to 570 (the
        // Iowa), and a scale the heavies overflow reads every card full.
        ["HULL", (t.hp / 600) * 100],
        ["SPEED", (t.speedMS / 22) * 100],
        ["ARMOR", ((t.beltMM ?? 0) / 120) * 100],
        ["GUNS", ((t.mainGun?.apDamage ?? 0) / 130) * 100],
        ["TORP", (t.torpedoes?.count ?? 0) * 25],
      ],
      need: t.unlockShips ?? 0, // ships to sink before this hull unlocks (0 = open)
      counter: "ships", // which career counter gates it (mixed rosters differ per card)
      pick: { gk: 11 + i, plane: 0 },
      preview: { hull: i },
    });

    // --- bomber crew stations: man a friendly bomber's defensive guns ---
    // One card per airframe and station (turretGk): seat indices are per
    // airframe — seat 0 is a Fortress's tail stinger and a Heinkel's dorsal
    // gun — so each card names its bomber, and the server seats you on the
    // first friendly one of THAT type (human pilots first) with the station
    // free. Titles lead with the airframe so three "Ventral Gunner" cards
    // never read as the same seat.
    const turretCards = [];
    types.forEach((t, ti) => {
      if (t.adCraft) return;
      const airframe = t.name.match(/^[A-Z][A-Za-z]*[ -]\d+/)?.[0] ?? t.name;
      (t.turrets ?? []).forEach((tu, si) => {
        turretCards.push({
          title: `${airframe} ${tu.name}`,
          role: `${tu.role ?? ""} · rides a friendly ${t.name}`,
          stats: [
            ["HULL", t.hpMult * 62], // shares the bomber's airframe
            ["ARC", Math.min(1, (tu.yawArcRad ?? 0) / Math.PI) * 100],
            ["TRACK", ((tu.trackRateRadPerSec ?? 2) / 2) * 100],
            // Weight of fire on the aircraft cards' scale (150 a second fills
            // it). A legacy twin mount (no `barrels`) spends the caliber's
            // rate between its pair; a named mount fires each barrel at it.
            ["GUNS", ((punchOf(tu.gun?.caliber) * (tu.barrels || 1)) / 150) * 100],
          ],
          pick: { gk: turretGk(ti, si), plane: 0 },
          preview: { plane: ti }, // the station rides the bomber: show her
        });
      });
    });

    // Per-type dispatch, the house rule: a hull's card is chosen by what the
    // hull IS (naval / land / the air game's AA park), never by which world
    // it happens to be parked in. Mixed rosters (the armada) get the right
    // card per row for free.
    const hullCard = (t, i) => (t.naval ? shipCard(t, i) : t.land ? armorCard(t, i) : vehCard(t, i));

    // In the naval and land theaters every human spawn is a HULL — the server
    // seats you in one no matter what the picker said (see w.naval || w.land
    // in world.go's spawn). So those lobbies show the hull roster and nothing
    // else: an aircraft card on the Brick Steppe was a promise the world broke
    // the moment you pressed ROLL OUT, and it took the carrier catapult, the
    // paint shop and the airfield's copy along with it.
    const naval = config.world?.theater === "naval";
    const land = config.world?.theater === "land";
    const hullOnly = naval || land;
    // Combined arms fields every unit class at once: planes lead (they're the
    // default spawn, as in the air game), then the warship and armor tabs,
    // split from the mixed roster by each type's class flag.
    const armada = config.world?.theater === "armada";
    // Each category carries an accent key (`cat`) so the combined-arms lobby
    // can color-code its arms — sky blue for the air war, teal for the fleet,
    // olive for the armor. The pure theaters never style it; harmless there.
    const categories = naval
      ? [{ label: "⚓ WARSHIP", cat: "sea", cards: vehTypes.map((t, i) => hullCard(t, i)) }]
      : land
      ? [{ label: "🛡 ARMOR", cat: "armor", cards: vehTypes.map((t, i) => hullCard(t, i)) }]
      : armada
        ? [
            { label: "✈ AIRCRAFT", cat: "air", cards: aircraftCards },
            ...(turretCards.length ? [{ label: "✪ BOMBER CREW", cat: "crew", cards: turretCards }] : []),
            {
              label: "⚓ WARSHIPS",
              cat: "sea",
              cards: vehTypes.flatMap((t, i) => (t.naval ? [hullCard(t, i)] : [])),
            },
            {
              label: "🛡 GROUND VEHICLES",
              cat: "armor",
              cards: vehTypes.flatMap((t, i) => (t.land ? [hullCard(t, i)] : [])),
            },
            {
              // The fixed emplacements: HQ/port flak, and the coast batteries
              // (plus Fort Brick's main gun, once your side holds zone D).
              label: "☄ GUNS",
              cat: "guns",
              cards: [
                gunCard(config.aaa, "Land Flak", "Fixed 88 mm anti-air emplacement", 1, "flak"),
                bigGunsCard,
              ],
            },
          ]
        : [
          { label: "✈ AIRCRAFT", cat: "air", cards: aircraftCards },
          ...(turretCards.length ? [{ label: "✪ BOMBER CREW", cat: "crew", cards: turretCards }] : []),
          // The emplacement tabs are AIR-theater seats: only initAAGuns arms a
          // map with fixed mounts, so they belong to this branch alone (the
          // hull theaters, which stock none, now have lobbies of their own).
          { label: "☄ AAA", cat: "guns", cards: [gunCard(config.aaa, "Land Flak", "Fixed 88 mm anti-air emplacement", 1, "flak")] },
          {
            label: "⚓ NAVY",
            cat: "sea",
            cards: [gunCard(config.navy, "Naval Mount", "Carrier-deck twin AA mount", 2, "navy"), bigGunsCard],
          },
          { label: "🛡 GROUND VEHICLES", cat: "armor", cards: vehTypes.map((t, i) => hullCard(t, i)) },
        ];

    const tabsRoot = this.el.unitTabs;
    const cardsRoot = this.el.unitSelect;
    tabsRoot.innerHTML = "";
    cardsRoot.innerHTML = "";
    const clamp = (v) => Math.max(0, Math.min(100, Math.round(v)));

    // Career-gated cards: a card whose unlock requirement exceeds the pilot's
    // progress shows locked and can't be picked until earned. Aircraft gate on
    // lifetime kills (mirrors the server's planeUnlocked backstop); naval hulls
    // gate on ships sunk. Only one theater is live at a time, so pick the
    // matching counter.
    if (this._careerKills == null) this._careerKills = 0;
    if (this._careerShips == null) this._careerShips = 0;
    if (!this._unlockedNames) this._unlockedNames = new Set();
    const isLocked = (card) => {
      const need = card.need ?? 0;
      if (need <= 0 || this._unlockedNames.has(card.title)) return false;
      const gateShips = card.counter === "ships" || (naval && card.counter !== "kills");
      const have = gateShips ? this._careerShips : this._careerKills;
      return need > have;
    };

    // Which tab is showing, and which card is the live pick (defaults to the
    // saved aircraft — bumped off a locked card so the highlight is flyable).
    let activeCat = 0;
    let pickedCat = 0;
    // A hull theater (naval, land) opens on the first hull the crew has
    // actually unlocked, and REGISTERS it — the pick has to travel out through
    // onPick, or the lobby keeps flying the saved aircraft's flags (the deck
    // catapult, the paint shop, "taking off…") over a world of ships and
    // tanks. Air/armada default to the saved aircraft, bumped off a locked
    // card so the highlight is flyable.
    let pickedCard = hullOnly ? 0 : Math.max(0, aircraftCards.indexOf(aircraftCardFor(selectedPlane)));
    if (hullOnly) {
      const first = categories[0].cards.findIndex((c) => !isLocked(c));
      pickedCard = first < 0 ? 0 : first;
      if (categories[0].cards[pickedCard]) onPick(categories[0].cards[pickedCard].pick);
    } else if (isLocked(aircraftCards[pickedCard] ?? {})) {
      pickedCard = 0;
    }


    // The old picker was a wall of stat cards — five at once on the air deck,
    // more on the combined screen — that buried the callsign box and TAKE OFF
    // under homework (player feedback: "super overwhelming"). Every lobby now
    // shows ONE card in the spotlight with arrows to walk the hangar and a dot
    // rail for the rest. Every card stays in the DOM (hidden, not absent) so
    // everything that drives the picker by clicking cards — the keyboard plane
    // switcher, the e2e harness — works unchanged.
    const carousel = true;
    // Where the carousel spotlight is (carousel mode only). Viewing IS
    // picking: arrowing onto an unlocked card selects it, so what you see is
    // what you spawn. A locked card can still be admired — the pick stays put.
    let viewIdx = pickedCard;

    const renderCards = () => {
      const cat = categories[activeCat];
      cardsRoot.innerHTML = "";
      this._spotCard = null;
      this._bayHost = null;
      if (cat.cat) cardsRoot.dataset.cat = cat.cat;
      else delete cardsRoot.dataset.cat;
      cardsRoot.classList.toggle("carousel", carousel);
      if (carousel && viewIdx >= cat.cards.length) viewIdx = 0;
      const stage = carousel ? document.createElement("div") : cardsRoot;
      if (carousel) stage.className = "car-stage";
      cat.cards.forEach((card, ci) => {
        const el = document.createElement("div");
        const locked = isLocked(card);
        const isPicked = !locked && activeCat === pickedCat && ci === pickedCard;
        el.className =
          "plane-card" +
          (isPicked ? " selected" : "") +
          (locked ? " locked" : "") +
          (carousel && ci !== viewIdx ? " off" : "");
        // The spotlight card opens on the hangar bay: the unit itself, on a
        // turntable (hangar_preview.js). Locked ones too — that's the point.
        const bayed = carousel && ci === viewIdx && !!this.hangarBay && !!card.preview;
        if (bayed) el.classList.add("bayed");
        el.innerHTML = `
          ${bayed ? `<div class="car-bay"><button type="button" class="bay-cap hidden"></button></div>` : ""}
          <h3>${esc(card.title)}</h3>
          <span class="role">${esc(card.role)}</span>
          ${card.stats
            .map(
              ([label, pct]) =>
                `<div class="stat"><label>${label}</label><div class="bar"><div class="fill" style="width:${clamp(pct)}%"></div></div></div>`,
            )
            .join("")}
          ${locked ? `<span class="lock-badge">🔒 ${card.counter === "ships" || naval ? `SINK ${card.need} SHIPS` : `${this._careerKills}/${card.need} KILLS`}</span>` : ""}`;
        el.onclick = () => {
          if (isLocked(card)) return; // can't crew a unit you haven't earned
          pickedCat = activeCat;
          pickedCard = ci;
          if (carousel) viewIdx = ci;
          onPick(card.pick);
          renderCards();
        };
        stage.appendChild(el);
        if (bayed) {
          // A swatch tapped on (touch has no mouseleave) is tried on the card
          // it was tapped over, not carried down the line to the next one.
          if (card !== this._lastSpot) this._paintPeek = null;
          this._lastSpot = card;
          this._spotCard = card;
          this._bayHost = el.querySelector(".car-bay");
          this.hangarBay.mount(this._bayHost, card.preview, this._bayScheme(card));
          this._bayHost.dataset.state = this.hangarBay.state ?? "loading";
          this._syncBay();
        }
      });

      if (carousel) {
        // Point the spotlight somewhere else: arrows wrap within the arm, and
        // landing on an unlocked card takes it (view = pick).
        const jumpTo = (ci) => {
          viewIdx = ci;
          const card = cat.cards[viewIdx];
          if (card && !isLocked(card)) {
            pickedCat = activeCat;
            pickedCard = viewIdx;
            onPick(card.pick);
          }
          renderCards();
        };
        const arrow = (d, glyph, label) => {
          const b = document.createElement("button");
          b.type = "button";
          b.className = "car-arrow";
          b.setAttribute("aria-label", label);
          b.textContent = glyph;
          b.disabled = cat.cards.length <= 1;
          b.onclick = () => jumpTo((viewIdx + d + cat.cards.length) % cat.cards.length);
          return b;
        };
        cardsRoot.appendChild(arrow(-1, "‹", "previous unit"));
        cardsRoot.appendChild(stage);
        cardsRoot.appendChild(arrow(1, "›", "next unit"));
        // The dot rail: one dot per hull in this arm, plus a "2 / 6" so the
        // hangar has a visible size before you've walked it.
        const rail = document.createElement("div");
        rail.className = "car-rail";
        cat.cards.forEach((card, ci) => {
          const dot = document.createElement("button");
          dot.type = "button";
          dot.className =
            "car-dot" + (ci === viewIdx ? " current" : "") + (isLocked(card) ? " locked" : "");
          dot.title = card.title;
          dot.setAttribute("aria-label", card.title);
          dot.onclick = () => jumpTo(ci);
          rail.appendChild(dot);
        });
        const count = document.createElement("span");
        count.className = "car-count";
        count.textContent = `${Math.min(viewIdx + 1, cat.cards.length)} / ${cat.cards.length}`;
        rail.appendChild(count);
        cardsRoot.appendChild(rail);
      }
      this._syncBay(); // the bay's caption, now its card is on the page
    };
    this._renderUnitCards = renderCards;
    this._cardLocked = isLocked;

    categories.forEach((cat, idx) => {
      const tab = document.createElement("button");
      tab.className = "unit-tab" + (idx === 0 ? " active" : "");
      tab.textContent = cat.label;
      if (cat.cat) tab.dataset.cat = cat.cat;
      tab.onclick = () => {
        activeCat = idx;
        if (carousel) {
          // Opening an arm points the spotlight at your pick there, or at the
          // first unit you've earned — and takes it (view = pick), so tabbing
          // to WARSHIPS means the fleet gets you.
          viewIdx =
            pickedCat === idx
              ? pickedCard
              : Math.max(0, cat.cards.findIndex((c) => !isLocked(c)));
          const card = cat.cards[viewIdx];
          if (card && !isLocked(card) && (pickedCat !== idx || pickedCard !== viewIdx)) {
            pickedCat = idx;
            pickedCard = viewIdx;
            onPick(card.pick);
          }
        }
        [...tabsRoot.children].forEach((t, i) => t.classList.toggle("active", i === idx));
        renderCards();
      };
      tabsRoot.appendChild(tab);
    });

    // Programmatic selection so the keyboard (1–6 to switch plane while down)
    // can drive the same highlight a click does, without re-firing onPick.
    // Matches a card by its wire pick {gk, plane} and flips to its tab.
    this.selectUnit = (gk, plane = 0) => {
      for (let ci = 0; ci < categories.length; ci++) {
        const idx = categories[ci].cards.findIndex(
          (card) => card.pick.gk === gk && (gk !== 0 || card.pick.plane === plane),
        );
        if (idx < 0 || isLocked(categories[ci].cards[idx])) continue;
        activeCat = ci;
        pickedCat = ci;
        pickedCard = idx;
        if (carousel) viewIdx = idx;
        [...tabsRoot.children].forEach((t, i) => t.classList.toggle("active", i === ci));
        renderCards();
        return;
      }
    };

    renderCards();

    // Lock check by aircraft index, for the keyboard plane switcher.
    // An index with no card (the ad circus) is never yours to fly.
    this.isAircraftLocked = (idx) => {
      const card = aircraftCardFor(idx);
      return !card || isLocked(card);
    };
  }

  /** The GUNS row (harmonize.js): where the picked airframe's battery
   * converges, the pilot's call — a slider in the armorer's 25 m clicks, ±
   * buttons for a thumb, FACTORY to put it back, a top-down sketch of the
   * streams crossing and one line on what the range buys and costs.
   * `onSet(type, meters)` fires on every change (0 = back to factory) and
   * returns the range the guns now converge at. Built once; setGunsPlane
   * points it at an airframe. */
  buildGunsRow(onSet) {
    const root = this.el.gunsRow;
    if (!root) return;
    root.innerHTML = `
      <div class="guns-body">
        <div class="guns-head"><b class="guns-plane"></b> <span class="guns-cal"></span></div>
        <div class="guns-dial">
          <button type="button" class="guns-step" data-d="-1" aria-label="converge closer">−</button>
          <input type="range" class="guns-slider" min="${CONV_MIN_M}" max="${CONV_MAX_M}" step="${CONV_STEP_M}" aria-label="gun convergence range" />
          <button type="button" class="guns-step" data-d="1" aria-label="converge farther">+</button>
          <span class="guns-read"></span>
          <button type="button" class="guns-factory ghost">FACTORY</button>
        </div>
        <div class="guns-sketch"></div>
        <div class="guns-hint"></div>
      </div>`;
    const q = (c) => root.querySelector(c);
    const g = (this._guns = {
      type: null,
      m: 0,
      plane: q(".guns-plane"),
      cal: q(".guns-cal"),
      slider: q(".guns-slider"),
      read: q(".guns-read"),
      factory: q(".guns-factory"),
      sketch: q(".guns-sketch"),
      hint: q(".guns-hint"),
    });
    const set = (m) => {
      if (!g.type) return;
      g.m = onSet(g.type, m);
      this._paintGuns();
    };
    // Dragging redraws live; the order goes out once, on release.
    g.slider.addEventListener("input", () => {
      g.m = Number(g.slider.value);
      this._paintGuns(false);
    });
    g.slider.addEventListener("change", () => set(Number(g.slider.value)));
    for (const b of root.querySelectorAll(".guns-step")) {
      b.onclick = () => {
        // One click along the armorer's grid — off an odd factory number
        // (280, 340) that's the next grid mark that way, not a 25 m jump.
        const up = Number(b.dataset.d) > 0;
        const k = g.m / CONV_STEP_M;
        set((up ? Math.floor(k) + 1 : Math.ceil(k) - 1) * CONV_STEP_M);
      };
    }
    g.factory.onclick = () => set(0);
  }

  /** Point the GUNS row at an airframe (`type` = aircraftTypes entry,
   * `calLabel` its battery's label, e.g. "6 × .50 Browning"). Hides the row
   * for a type with no fixed guns. */
  setGunsPlane(type, calLabel = "") {
    const g = this._guns;
    if (!g) return;
    g.type = factoryConvergence(type) > 0 ? type : null;
    this._gunsHasType = !!g.type;
    this._syncGunsVisible();
    if (!g.type) return;
    g.m = convergenceFor(type);
    g.plane.textContent = type.name;
    // A real battery reads its whole loadout ("2 × 20 mm Hispano + 4 ×
    // .303 Browning", battery.js batteryLabel); a lone legacy caliber label
    // says whether it's a pair or a single.
    const n = type.gun?.barrels ?? 2;
    const count = /×|quad/.test(calLabel) ? "" : n === 1 ? "single " : "twin ";
    g.cal.textContent = `· ${count}${calLabel}`;
    this._paintGuns();
  }

  /** Re-read the GUNS row's range (the welcome may have brought the
   * career's harmonization in over this device's). */
  refreshGuns() {
    const g = this._guns;
    if (!g?.type) return;
    g.m = convergenceFor(g.type);
    this._paintGuns();
  }

  _paintGuns(syncSlider = true) {
    const g = this._guns;
    if (!g?.type) return;
    const factory = factoryConvergence(g.type);
    if (syncSlider) g.slider.value = String(g.m);
    g.read.textContent = `${g.m} m`;
    g.read.classList.toggle("custom", g.m !== factory);
    g.factory.disabled = g.m === factory;
    for (const b of g.slider.parentElement.querySelectorAll(".guns-step")) {
      b.disabled = Number(b.dataset.d) > 0 ? g.m >= CONV_MAX_M : g.m <= CONV_MIN_M;
    }
    g.factory.title = `factory boresight: ${factory} m`;
    g.sketch.innerHTML = convergenceSketch(g.type, g.m, factory);
    g.hint.textContent = convergenceAdvice(g.type, g.m);
  }

  /** Show/hide the GUNS row (aircraft spawns only). */
  setGunsVisible(v) {
    this._gunsWanted = !!v;
    this._syncGunsVisible();
  }

  _syncGunsVisible() {
    this.el.gunsRow?.classList.toggle("hidden", !(this._gunsWanted && this._gunsHasType));
  }

  /** The PAINT SHOP row (roadmap #25): one swatch per earnable scheme, locked
   * until the career counter it gates on is met. `career` is the PilotRecord
   * (its JSON keys match the requirement stat names); `equipped` is the current
   * scheme index; `onEquip(i)` is called when an unlocked swatch is clicked.
   * Cosmetic only — locks here mirror the server's SetScheme backstop.
   * A livery painted for one airframe (`model`) only shows while that
   * airframe is picked (setPaintModel). */
  buildPaintRow(schemes, career, equipped, onEquip) {
    const root = this.el.paintRow;
    if (!root) return;
    root.innerHTML = "";
    // Nothing to choose (no config, or only the default) — keep it hidden.
    if (!schemes || schemes.length <= 1) {
      root.classList.add("hidden");
      return;
    }
    this._paintEquipped = equipped | 0;
    const need = (sc) => sc.requires?.need ?? 0;
    const stat = (sc) => sc.requires?.stat ?? "";
    const locked = (sc) => need(sc) > 0 && stat(sc) !== "" && (career?.[stat(sc)] ?? 0) < need(sc);

    const label = document.createElement("span");
    label.className = "paint-label";
    label.textContent = "PAINT";
    root.appendChild(label);
    const swatches = document.createElement("div");
    swatches.className = "paint-swatches";
    root.appendChild(swatches);

    // One quiet line under the swatches that answers "how do I get that one?"
    // — filled on hover/click of a locked swatch, because a bare 🔒 with no
    // story is a door with no handle (user feedback #11). The native title
    // carries the same text for tooltip readers.
    const hint = document.createElement("div");
    hint.className = "paint-hint";

    // The hangar bay wears whatever swatch is under the pointer (a locked one
    // too: the picture of what the kills buy), and its caption says so.
    this._paintInfo = {
      schemes,
      locked: (i) => !!schemes[i] && locked(schemes[i]),
      progress: (i) => {
        const sc = schemes[i];
        return sc ? `${career?.[stat(sc)] ?? 0}/${need(sc)} ${statLabel(stat(sc))}` : "";
      },
    };
    this._paintPeek = null;

    schemes.forEach((sc, i) => {
      const b = document.createElement("button");
      const lk = locked(sc);
      const unlockText = lk
        ? `${sc.name} — ${career?.[stat(sc)] ?? 0}/${need(sc)} ${statLabel(stat(sc))} to unlock`
        : "";
      b.type = "button";
      b.className = "paint-swatch" + (i === this._paintEquipped ? " equipped" : "") + (lk ? " locked" : "");
      b.title = lk ? `🔒 ${unlockText}` : sc.name;
      // Swatch fill: the scheme body colour, or a neutral chip for the factory
      // finish and stripe-only schemes. No team hue: no airframe wears one.
      // A livery shows its plate in three stripes: uppers (or the camouflage
      // laid over them), flank, and the theatre yellow (or whatever its nose
      // or band wears, or the fin a Navy scheme painted lighter, or a
      // Mustang's nose ring, or a Lightning's spinners).
      const lp = sc.paint;
      b.style.background = lp
        ? `linear-gradient(90deg, ${lp.Camo ?? lp.Upper} 0 30%, ${lp.Side} 30% 70%, ${lp.Cowl ?? lp.Band ?? lp.Side_Fin ?? lp.Side_Cowl ?? lp.Spinner ?? lp.Side} 70%)`
        : sc.body || (sc.tailStripe ? "#565c68" : "#767d89");
      b.dataset.model = sc.model ?? "";
      if (sc.tailStripe && !sc.body) b.style.borderRight = `4px solid ${sc.tailStripe}`;
      b.textContent = lk ? "🔒" : "";
      b.addEventListener("mouseenter", () => {
        if (lk) hint.textContent = `🔒 ${unlockText}`;
        this._peekPaint(i);
      });
      b.addEventListener("mouseleave", () => {
        if (lk) hint.textContent = "";
        this._peekPaint(null);
      });
      b.onclick = () => {
        if (lk) {
          hint.textContent = `🔒 ${unlockText}`; // tap on touch = the hover
          this._peekPaint(i); // ...and the bay tries it on
          return;
        }
        this._paintEquipped = i;
        this._paintPeek = null;
        this._paintFit();
        onEquip(i);
      };
      swatches.appendChild(b);
    });
    this._paintSwatches = swatches;
    this._paintFit();
    root.appendChild(hint);
    root.classList.remove("hidden");
  }

  /** The airframe the PAINT SHOP is dressing (a model stem: "bf109"): its own
   * liveries join the swatches, every other airframe's leave. `finish` is
   * what its factory finish is called when the model wears a plate of its own
   * (the Mustang rolls out as Pattie Ann), else null: the config's name. */
  setPaintModel(stem, finish = null) {
    this._paintModel = stem ?? "";
    this._paintFinish = finish ?? null;
    this._paintFit();
  }

  // A scheme's name on this airframe: the factory chip goes by the finish
  // the airframe actually rolls out in.
  _paintName(i) {
    const sc = this._paintInfo?.schemes[i];
    return i === 0 && this._paintFinish ? this._paintFinish : sc?.name;
  }

  _paintFit() {
    const sw = this._paintSwatches ? [...this._paintSwatches.children] : [];
    const off = (el) => !!el.dataset.model && el.dataset.model !== this._paintModel;
    sw.forEach((el) => el.classList.toggle("hidden", off(el)));
    if (sw[0] && !sw[0].classList.contains("locked")) sw[0].title = this._paintName(0) ?? "";
    // A livery equipped for another airframe flies as the factory finish here:
    // say so on the factory chip rather than lighting nothing.
    const eq = sw[this._paintEquipped | 0];
    const stood = !eq || off(eq);
    sw.forEach((el, j) => el.classList.toggle("equipped", stood ? j === 0 : j === (this._paintEquipped | 0)));
    this._syncBay();
  }

  /** The hangar bay (hangar_preview.js HangarBay) the carousel's spotlight
   * card shows its unit in. Set before buildUnitSelect; null = text cards. */
  setHangarBay(bay) {
    this.hangarBay = bay ?? null;
    if (bay) bay.onState = (st) => this._bayHost && (this._bayHost.dataset.state = st);
  }

  /** Try a swatch on in the hangar bay (null: back to the equipped paint). */
  _peekPaint(i) {
    this._paintPeek = i;
    this._syncBay();
  }

  // The paint the bay shows for a card: the swatch being tried on, else the
  // one equipped. Only airframes wear paint.
  _bayScheme(card) {
    if (card?.preview?.plane == null) return 0;
    return (this._paintPeek ?? this._paintEquipped ?? 0) | 0;
  }

  // Repaint the bay and write its caption: the livery on show, and — for one
  // still to earn — what it takes.
  _syncBay() {
    const bay = this.hangarBay;
    const card = this._spotCard;
    const host = this._bayHost;
    if (!bay || !card || !host?.isConnected) return;
    const plane = card.preview?.plane;
    const want = this._bayScheme(card);
    bay.setScheme(want);
    const cap = host.querySelector(".bay-cap");
    if (!cap) return;
    const info = this._paintInfo;
    const paintOn = !!info && !this.el.paintRow?.classList.contains("hidden");
    if (plane == null || !paintOn || (this._cardLocked?.(card) && this._paintPeek == null)) {
      cap.classList.add("hidden");
      return;
    }
    const shown = bay.fits(plane, want) ? want : 0;
    const sc = info.schemes[shown];
    const lk = info.locked(shown);
    // How many paints this airframe can wear beyond the one on show — the
    // invitation to open the paint shop.
    let more = 0;
    info.schemes.forEach((_, i) => {
      if (i !== shown && bay.fits(plane, i)) more++;
    });
    cap.classList.remove("hidden");
    cap.classList.toggle("locked", lk);
    cap.textContent = lk
      ? `🔒 ${(sc?.name ?? "").toUpperCase()} — ${info.progress(shown).toUpperCase()}`
      : `🎨 ${(this._paintName(shown) ?? "Factory Finish").toUpperCase()}${this._paintPeek == null && more ? ` · ${more} MORE` : ""}`;
    cap.title = "open the paint shop";
    cap.onclick = (e) => {
      e.stopPropagation(); // not a pick: the card is already the pick
      const row = this.el.paintRow;
      if (row && !row.classList.contains("open")) document.getElementById("paint-toggle")?.click();
      row?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    };
  }

  /** Show/hide the paint row (aircraft spawns only). */
  setPaintVisible(v) {
    this.el.paintRow?.classList.toggle("hidden", !v);
    this._syncBay();
  }

  /** UNIT INSIGNIA: the badge button beside the callsign and the grid it
   * opens. Every badge is free — it's who you fly as, not what you've earned
   * — so the grid is one tap from the name box on the join and respawn cards
   * alike. `onPick(id)` fires on a choice ("" = no badge). The images load the
   * first time the grid opens, not at boot: most pilots pick once. */
  buildInsigniaPicker(catalog, equipped, onPick) {
    const root = this.el.insigniaRow;
    const btn = this.el.insigniaBtn;
    if (!root || !btn) return;
    root.innerHTML = "";
    if (!catalog?.length) {
      btn.classList.add("hidden");
      root.classList.add("hidden");
      return;
    }
    btn.classList.remove("hidden");
    this._insEquipped = equipped || "";
    this._insPicks = new Map();

    // "No badge" lives in the header, so the grid itself is exactly the forty
    // and lays out in whole rows.
    const head = document.createElement("div");
    head.className = "ins-head";
    head.innerHTML = '<span class="ins-label">UNIT INSIGNIA <span class="ins-sub">· flies beside your callsign</span></span>';
    root.appendChild(head);
    const grid = document.createElement("div");
    grid.className = "ins-grid";
    root.appendChild(grid);
    const hint = document.createElement("div");
    hint.className = "ins-hint";
    root.appendChild(hint);
    const hintFor = (id) => {
      const d = insigniaDef(id);
      return d ? `${d.name.toUpperCase()} — ${d.motto ?? ""}` : "NO INSIGNIA — just the callsign";
    };
    const say = (id) => (hint.textContent = hintFor(id));

    const add = (id) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = (id ? "ins-pick" : "ins-none ghost") + (id === this._insEquipped ? " equipped" : "");
      b.dataset.ins = id;
      b.title = id ? insigniaDef(id).name : "no insignia";
      b.setAttribute("aria-label", b.title);
      if (id) {
        const img = document.createElement("img");
        img.alt = "";
        img.draggable = false;
        img.dataset.src = insigniaURL(id);
        b.appendChild(img);
      } else {
        b.textContent = "⌀ NONE";
      }
      b.addEventListener("mouseenter", () => say(id));
      b.addEventListener("focus", () => say(id));
      b.addEventListener("mouseleave", () => say(this._insEquipped));
      b.onclick = () => {
        this._insEquipped = id;
        for (const [pid, el] of this._insPicks) el.classList.toggle("equipped", pid === id);
        this.setInsigniaFace(id);
        say(id);
        onPick(id);
      };
      this._insPicks.set(id, b);
      (id ? grid : head).appendChild(b);
    };
    add("");
    for (const d of catalog) add(d.id);
    say(this._insEquipped);
    this.setInsigniaFace(this._insEquipped);
    btn.onclick = () => this.setInsigniaOpen(root.classList.contains("hidden"));
  }

  /** Open/close the insignia grid (loading its badges the first time). */
  setInsigniaOpen(open) {
    const root = this.el.insigniaRow;
    if (!root || !this._insPicks) return;
    root.classList.toggle("hidden", !open);
    this.el.insigniaBtn?.classList.toggle("active", open);
    if (open && !this._insLoaded) {
      this._insLoaded = true;
      for (const img of root.querySelectorAll("img[data-src]")) img.src = img.dataset.src;
    }
  }

  /** Show the equipped badge on the button beside the callsign (and mark it
   * in the grid when the choice came from the server, not a click). */
  setInsigniaFace(id) {
    const btn = this.el.insigniaBtn;
    if (!btn) return;
    const url = insigniaURL(id || "");
    btn.innerHTML = url ? `<img src="${url}" alt="" draggable="false">` : '<span class="ins-empty">+</span>';
    btn.title = url ? `unit insignia: ${insigniaDef(id).name}` : "pick a unit insignia";
    btn.setAttribute("aria-label", btn.title);
    if (this._insPicks && this._insEquipped !== (id || "")) {
      this._insEquipped = id || "";
      for (const [pid, el] of this._insPicks) el.classList.toggle("equipped", pid === this._insEquipped);
    }
  }

  /** Update the pilot's lifetime kills so the picker locks/unlocks the
   *  career-gated aircraft accordingly, re-rendering if it's built. */
  setCareerKills(n) {
    this._careerKills = n | 0;
    this._renderUnitCards?.();
  }

  /** Update the pilot's lifetime ships-sunk so the naval picker locks/unlocks
   *  hulls accordingly, re-rendering if the picker is built. */
  setCareerShips(n) {
    this._careerShips = n | 0;
    this._renderUnitCards?.();
  }

  /** Mark an aircraft type or hull class unlocked this session (from an
   *  "unlock" event), so the picker opens it live without a reconnect. */
  markUnlocked(name) {
    (this._unlockedNames ??= new Set()).add(name);
    this._renderUnitCards?.();
  }

  setProtect(visible) {
    this.el.protect.classList.toggle("hidden", !visible);
  }

  setWarning(text, danger = false) {
    this.el.warning.classList.toggle("hidden", !text);
    this.el.warning.classList.toggle("danger", !!text && danger);
    if (text) setText(this.el.warning, text);
  }

  // Tank main-gun reload readout under the crosshair. Pass null to hide,
  // { left } seconds while loading, or { left: 0 } when the gun is ready.
  setReload(state) {
    const el = this.el.reload;
    if (!el) return;
    if (!state) {
      el.classList.add("hidden");
      return;
    }
    el.classList.remove("hidden");
    if (state.left > 0) {
      setText(el, `RELOADING ${state.left.toFixed(1)}s`);
      el.classList.add("loading");
      el.classList.remove("ready");
    } else {
      setText(el, "● READY");
      el.classList.remove("loading");
      el.classList.add("ready");
    }
  }

  // Naval turret readout under the crosshair: how much of the main battery
  // bears on the current lay ("GUNS 4/6"), plus a MAX RANGE tag while the
  // sight is clamped at the guns' reach. `masked` counts the bearing barrels
  // whose rounds an island takes short of the ring, `every` says it takes
  // them all (gunsight.js navalSalvoMask): the director sees over a hill the
  // turrets can't, so the chip turns red and says so, with the way out — no
  // broadside helps a salvo that bursts on a rock. On a phone the chip rides
  // left of BAIL with room for about 31 characters, so a masked lay drops
  // the recovery and the MAX tag there: the red X on the island and the
  // slate ring say the rest, and the field manual teaches the way out. Pass
  // null to hide.
  setBattery(state) {
    const el = this.el.battery;
    if (!el) return;
    if (!state) {
      el.classList.add("hidden");
      return;
    }
    el.classList.remove("hidden");
    const touch = document.body.classList.contains("touch");
    const full = state.bearing >= state.total;
    const masked = Math.min(state.masked ?? 0, state.bearing);
    el.classList.toggle("partial", !full && !masked);
    el.classList.toggle("masked", masked > 0);
    let text = `GUNS ${state.bearing}/${state.total}`;
    if (masked > 0) {
      text += ` · ${state.every ? "" : `${masked} `}MASKED BY LAND`;
      if (!touch) text += " — steam clear";
    } else if (!full) {
      text += " — swing broadside";
    }
    if (state.max && !(touch && masked > 0)) text += " · MAX RANGE";
    setText(el, text);
  }

  setScoreboard(visible, roster, board, selfId) {
    this.el.scoreboard.classList.toggle("hidden", !visible);
    if (!visible) {
      this._boardAt = -Infinity; // the next TAB opens on fresh standings
      return;
    }
    // Held open (TAB, or the end-of-round ledger) it's asked for every frame,
    // but kills land a few times a minute: rebuild four times a second, and
    // swap the markup in only when a row actually changed.
    const now = performance.now();
    if (now - (this._boardAt ?? -Infinity) < 250) return;
    this._boardAt = now;
    // Two team ledgers side by side (Crimson left, Cobalt right): half the
    // height of the old mixed table, and the halves make the standings between
    // the squadrons readable at a glance.
    const rows = [...roster.values()]
      .filter((r) => !r.ad) // the ad circus flies for no team and no ladder
      .map((r) => ({ ...r, ...(board.get(r.id) ?? { k: 0, d: 0, w: 0 }) }))
      .sort((a, b) => b.k - a.k);
    const render = (team) =>
      rows
        .filter((r) => r.team === team)
        .map(
          (r) => `<tr class="${r.id === selfId ? "me" : ""}">
          <td>${insigniaHTML(r.ins)}${esc(r.name)}${
            r.bot
              ? ' <span class="bot-tag">[bot]</span>'
              : r.title
                ? ` <span class="title-tag">${esc(r.title)}</span>`
                : ""
          }</td>
          <td>${r.k}</td><td>${r.d}</td><td>${r.bot ? "—" : (r.w ?? 0)}</td></tr>`,
        )
        .join("");
    setHTML(this.el.scoreboardCrimson, render(0));
    setHTML(this.el.scoreboardCobalt, render(1));
  }

  /** Pre-render the island silhouette once; blips get drawn over it. */
  buildMinimapBase(field) {
    // 100 px reads fine on the 5 km air map, but at the 9 km gunnery scale a
    // pixel is 180 m — Fort Brick, a whole capture-zone island, dissolved
    // into two faint dots and the map read as "a ring on open water".
    const n = field.radius >= 9000 ? 170 : 100;
    const c = document.createElement("canvas");
    c.width = n;
    c.height = n;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#16456e";
    ctx.fillRect(0, 0, n, n);
    for (let py = 0; py < n; py++) {
      for (let px = 0; px < n; px++) {
        const x = ((px + 0.5) / n - 0.5) * 2 * field.radius;
        const z = ((py + 0.5) / n - 0.5) * 2 * field.radius;
        if (x * x + z * z > field.radius * field.radius) {
          ctx.fillStyle = "#0d2a44";
          ctx.fillRect(px, py, 1, 1);
          continue;
        }
        const h = field.heightAt(x, z);
        // Trace the road network (a pixel here is ~100 m, so widen the test).
        const road = h <= 96 && roadDistance(field, x, z) <= ROAD_HALF_WIDTH + 40;
        if (h > 96) ctx.fillStyle = "#8a8d90";
        else if (h > 8) ctx.fillStyle = road ? "#5d5a52" : "#4d8a37";
        // Match the 3D world: columns quantized to sea level render as water.
        else if (h >= field.blockHeight) ctx.fillStyle = road ? "#5d5a52" : "#bfae62";
        else if (road) ctx.fillStyle = "#7a6a4f"; // bridge over water
        else continue;
        ctx.fillRect(px, py, 1, 1);
      }
    }
    // Team territories: the x+z=0 anti-diagonal splits the map, crimson
    // holding the top-left half. A faint tint sells whose flak you're over.
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = "#e84a4a";
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(n, 0);
    ctx.lineTo(0, n);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#4a7de8";
    ctx.beginPath();
    ctx.moveTo(n, 0);
    ctx.lineTo(n, n);
    ctx.lineTo(0, n);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
    this.mapBase = c;
  }

  /** Remember the static ship anchors for the minimap. */
  setShips(ships) {
    this.ships = ships ?? [];
  }

  /** Naval domination capture-zone centres [{x, z}] + radius, for the minimap. */
  setNavalZones(centers, radiusM) {
    this.navalZones = centers ?? [];
    this.navalZoneR = radiusM ?? 0;
  }

  /** Floating survivors for the minimap (roadmap #2): [{x, z, team}]. */
  setRescues(markers) {
    this.rescues = markers ?? [];
  }

  /** Naval fall-of-shot markers for the minimap: [{x, z, age}] (N2). */
  setNavalImpacts(impacts) {
    this.navalImpacts = impacts ?? [];
  }

  /** Live supply-truck blips for the minimap: [{x, z, team}]. */
  setTrucks(trucks) {
    this.trucksBlips = trucks ?? [];
  }

  /** Live supply-ship blips for the minimap: [{x, z, team}]. */
  setSupplyShips(ships) {
    this.supplyShipBlips = ships ?? [];
  }

  /** Live armored-vehicle blips for the minimap: [{x, z, team}]. selfTeam
   *  (when given) lets the draw loop pop ENEMY hulls — the blips a hull
   *  theater's player is actually hunting — a size up with an outline. */
  setVehicles(vehicles, selfTeam = null) {
    this.vehiclesBlips = vehicles ?? [];
    this.vehiclesSelfTeam = selfTeam;
  }

  /** Battleship main-battery mode: swap the corner HUD / touch cluster for the
   *  full-screen plot table. */
  setManningMain(on) {
    document.body.classList.toggle("manning-main", on);
  }

  /** Show or hide the plot table (hidden during the firing cutaway). */
  setPlotVisible(on) {
    const t = this.el.plotTable;
    const was = !t.classList.contains("hidden");
    t.classList.toggle("hidden", !on);
    if (on && !was) this.resizePlot();
    if (!on) this.hideLoupe();
  }

  /** Size the plot canvas as large as the viewport allows, reserving only the
   *  measured height of the status banner + action bar so short landscape
   *  phones still get a big chart. */
  resizePlot() {
    // The chart fills the viewport height (it's square, so the width follows).
    // The status banner and action bar float inside the frame, so no vertical
    // room is reserved for them.
    const margin = 16;
    const size = Math.max(
      240,
      Math.round(Math.min(window.innerWidth - margin, window.innerHeight - margin)),
    );
    const c = this.el.plotCanvas;
    c.width = c.height = size;
    c.style.width = c.style.height = size + "px";
  }

  /** Fire-control status banner above the plot table. */
  setFcs(text, ready = false) {
    setText(this.el.plotStatus, text);
    this.el.plotBanner.classList.toggle("ready", ready);
  }

  /** Fire-control solution readout: the data points the ballistic computer
   *  settled on for the plotted target (powder charge, quadrant elevation,
   *  range/bearing, time of flight, …). Pass an array of
   *  { label, value, tone? } rows to show the slip, or null/[] to hide it.
   *  tone: "hot" (dropped charge / plunging fire) | "good" (spotter tightened). */
  setFireSolution(rows) {
    const el = this.el.plotReadout;
    if (!rows || rows.length === 0) {
      el.classList.add("hidden");
      el.setAttribute("aria-hidden", "true");
      return;
    }
    // Reuse existing dt/dd nodes where we can to avoid churning the DOM every
    // frame; only rebuild when the row count changes.
    const dl = this.el.plotReadoutRows;
    if (dl.childElementCount !== rows.length * 2) {
      dl.replaceChildren();
      for (let i = 0; i < rows.length; i++) {
        dl.appendChild(document.createElement("dt"));
        dl.appendChild(document.createElement("dd"));
      }
    }
    for (let i = 0; i < rows.length; i++) {
      const dt = dl.children[i * 2], dd = dl.children[i * 2 + 1];
      setText(dt, rows[i].label);
      setText(dd, rows[i].value);
      setClass(dd, rows[i].tone || "");
    }
    el.classList.remove("hidden");
    el.setAttribute("aria-hidden", "false");
  }

  /** Drive the plot table's primary button + RE-AIM affordance.
   *  spec: { label, kind: ""|"set"|"fire", enabled, reaim }. */
  setPlotActions(spec) {
    const b = this.el.plotPrimary;
    b.textContent = spec.label;
    b.disabled = !spec.enabled;
    b.dataset.kind = spec.kind || "";
    b.classList.toggle("fire", spec.kind === "fire");
    b.classList.toggle("set", spec.kind === "set");
    this.el.plotReaim.classList.toggle("hidden", !spec.reaim);
  }

  /** Convert a pointer event on the plot canvas into world XZ. */
  plotPointToWorld(ev) {
    const v = this.plotView;
    if (!v) return null;
    const rect = this.el.plotCanvas.getBoundingClientRect();
    const px = ((ev.clientX - rect.left) / rect.width) * v.size;
    const py = ((ev.clientY - rect.top) / rect.height) * v.size;
    return {
      x: (px / v.size * 2 - 1) * v.r + v.cx,
      z: (py / v.size * 2 - 1) * v.r + v.cz,
      px, py,
    };
  }

  /** Magnifier loupe that follows the dragging finger, sampling the plot
   *  canvas around the aim point and floating above the touch. */
  showLoupe(clientX, clientY, canvasPx, canvasPy) {
    const el = this.el.plotLoupe;
    const lw = el.width;
    const lc = this.plotLoupeCtx;
    const span = 56; // world window sampled, in canvas px → ~2.5× zoom
    lc.clearRect(0, 0, lw, lw);
    // Smooth the magnified slice — the chart underneath is now high-resolution,
    // so the loupe reads as a clean paper crop rather than a block of pixels.
    lc.imageSmoothingEnabled = true;
    lc.imageSmoothingQuality = "high";
    lc.drawImage(this.el.plotCanvas, canvasPx - span / 2, canvasPy - span / 2, span, span, 0, 0, lw, lw);
    const arm = lw * 0.1;
    lc.strokeStyle = "rgba(244, 236, 218, 0.85)";
    lc.lineWidth = 4;
    lc.beginPath();
    lc.moveTo(lw / 2 - arm, lw / 2); lc.lineTo(lw / 2 + arm, lw / 2);
    lc.moveTo(lw / 2, lw / 2 - arm); lc.lineTo(lw / 2, lw / 2 + arm);
    lc.stroke();
    lc.strokeStyle = "#a4561a";
    lc.lineWidth = 2;
    lc.beginPath();
    lc.moveTo(lw / 2 - arm, lw / 2); lc.lineTo(lw / 2 + arm, lw / 2);
    lc.moveTo(lw / 2, lw / 2 - arm); lc.lineTo(lw / 2, lw / 2 + arm);
    lc.stroke();
    el.classList.remove("hidden");
    el.style.left = clientX - lw / 2 + "px";
    el.style.top = clientY - 118 - lw / 2 + "px";
  }

  hideLoupe() {
    this.el.plotLoupe.classList.add("hidden");
  }

  drawMinimap(field, zoneRadius, players, selfId, selfPos, selfYaw, plot = null, pings = null,
              ctx = this.minimapCtx, size = this.el.minimap.width, heat = null) {
    const toMap = (x, z) => [
      ((x / field.radius) * 0.5 + 0.5) * size,
      ((z / field.radius) * 0.5 + 0.5) * size,
    ];
    const toMapR = (r) => (r / field.radius) * (size / 2);
    if (this.mapBase) ctx.drawImage(this.mapBase, 0, 0, size, size);

    // Kill-heat blobs for the spawn-screen tactical picture (roadmap #55):
    // soft red radial gradients where the recent killing has been.
    for (const h of heat ?? []) {
      const a = 0.5 * h.w * (1 - h.age / 45);
      if (a <= 0) continue;
      const [hx, hy] = toMap(h.x, h.z);
      const r = size * 0.09;
      const g = ctx.createRadialGradient(hx, hy, 0, hx, hy, r);
      g.addColorStop(0, `rgba(255, 80, 40, ${a.toFixed(2)})`);
      g.addColorStop(1, "rgba(255, 80, 40, 0)");
      ctx.fillStyle = g;
      ctx.fillRect(hx - r, hy - r, r * 2, r * 2);
    }

    // Floating survivors (roadmap #2): a pulsing orange life-ring blip both
    // teams can steer for. Team-tinted core so you know whose pilot it is.
    const rTime = (this.mapTime = (this.mapTime ?? 0) + 0.016);
    for (const m of this.rescues ?? []) {
      const [mx, my] = toMap(m.x, m.z);
      const pulse = 4 + Math.sin(rTime * 5 + m.x * 0.02) * 1.6;
      ctx.beginPath();
      ctx.arc(mx, my, pulse, 0, Math.PI * 2);
      ctx.strokeStyle = "#ffa030";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = m.team === 0 ? "#ff6b6b" : "#6b9bff";
      ctx.beginPath();
      ctx.arc(mx, my, 1.6, 0, Math.PI * 2);
      ctx.fill();
    }

    // Quick-chat pings (roadmap #17): pulsing green pins that age out.
    for (const pg of pings ?? []) {
      const a = 1 - pg.age / pg.ttl;
      if (a <= 0) continue;
      const [mx, my] = toMap(pg.x, pg.z);
      const pulse = 4 + Math.sin(pg.age * 5) * 1.5;
      ctx.globalAlpha = 0.35 + 0.65 * a;
      this.drawPin(ctx, mx, my, pulse, "#5fe07a", "#1f6b34");
      ctx.globalAlpha = 1;
    }

    if (this.navalZones?.length) {
      // Naval domination: a ring per capture zone, coloured by who holds it
      // (gold = neutral/contested), so the whole objective reads at a glance.
      const zc = { "-1": "#f4c542", 0: "#ff6b6b", 1: "#6b9bff" };
      const holders = this.navalZoneHolders ?? [];
      ctx.lineWidth = 2;
      for (let i = 0; i < this.navalZones.length; i++) {
        const c = this.navalZones[i];
        const [zx, zy] = toMap(c.x, c.z);
        ctx.strokeStyle = zc[holders[i] ?? -1] ?? "#f4c542";
        ctx.beginPath();
        ctx.arc(zx, zy, toMapR((c.r > 0 ? c.r : this.navalZoneR) || zoneRadius), 0, Math.PI * 2);
        ctx.stroke();
      }
    } else if (this.brickton?.city) {
      // Battle of Brickton: the town is the objective, not the ring — a
      // block of the defender's colour with a ring that fills as it burns.
      const bk = this.brickton;
      const [cx, cy] = toMap(bk.city.x, bk.city.z);
      const r = Math.max(5, toMapR(bk.city.r ?? 240));
      ctx.fillStyle = bk.attacker === 0 ? "rgba(107,155,255,0.55)" : "rgba(255,107,107,0.55)";
      ctx.fillRect(cx - r * 0.7, cy - r * 0.7, r * 1.4, r * 1.4);
      ctx.strokeStyle = "#5a4a3a";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = "#ff8a2a";
      ctx.beginPath();
      ctx.arc(cx, cy, r + 3, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.min(1, bk.burn / (bk.burnToWin || 0.7)));
      ctx.stroke();
    } else {
      // Sky Ring.
      ctx.strokeStyle = "#f4c542";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, (zoneRadius / field.radius) * (size / 2), 0, Math.PI * 2);
      ctx.stroke();
    }

    // N2: fall-of-shot markers — where your last salvos landed, fading with age.
    // WoWs-style "did my shells land short or over" awareness beyond visual range.
    if (this.navalImpacts?.length) {
      for (const im of this.navalImpacts) {
        const [ix, iy] = toMap(im.x, im.z);
        ctx.fillStyle = `rgba(255,255,255,${Math.max(0, 1 - (im.age ?? 0) / 6) * 0.9})`;
        ctx.fillRect(ix - 1, iy - 1, 2, 2);
      }
    }

    // Naval groups: chunky blips for the capital ships, dots for escorts;
    // sunk hulls grey out until they refloat. In fleet action the flagships
    // wear a ring so everyone knows which hull the round hangs on.
    for (const s of this.ships ?? []) {
      const [mx, my] = toMap(s.x, s.z);
      const color =
        s.alive === false ? "#5a6068" : s.team === 0 ? "#ff9b9b" : "#9bbcff";
      ctx.fillStyle = color;
      if (s.kind === 0 || s.kind === 2) ctx.fillRect(mx - 3, my - 1.5, 6, 3);
      else ctx.fillRect(mx - 1.5, my - 1, 3, 2);
      if (this.flagshipKind != null && s.kind === this.flagshipKind) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(mx, my, 5.5, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    // Supply convoys: tiny dots crawling the highway.
    for (const t of this.trucksBlips ?? []) {
      const [mx, my] = toMap(t.x, t.z);
      ctx.fillStyle = t.team === 0 ? "#ffb27a" : "#7ad0ff";
      ctx.fillRect(mx - 1, my - 1, 2, 2);
    }

    // Supply ships: small chevrons crawling the friendly sea lanes.
    for (const s of this.supplyShipBlips ?? []) {
      const [mx, my] = toMap(s.x, s.z);
      ctx.fillStyle = s.team === 0 ? "#ffb27a" : "#7ad0ff";
      ctx.fillRect(mx - 2, my - 1, 4, 2);
    }

    // Armored columns: slightly chunkier square dots on the highway. Enemy
    // hulls (vs. the viewer's team, when known) pop a size up with a white
    // rim — they're the targets a hull theater's player is hunting.
    for (const v of this.vehiclesBlips ?? []) {
      const [mx, my] = toMap(v.x, v.z);
      const enemy = this.vehiclesSelfTeam != null && v.team !== this.vehiclesSelfTeam;
      ctx.fillStyle = v.team === 0 ? "#ff7a4a" : "#4ab8ff";
      if (enemy) {
        ctx.fillRect(mx - 2.5, my - 2.5, 5, 5);
        ctx.strokeStyle = "rgba(255,255,255,0.8)";
        ctx.lineWidth = 1;
        ctx.strokeRect(mx - 2.5, my - 2.5, 5, 5);
      } else {
        ctx.fillRect(mx - 1.5, my - 1.5, 3, 3);
      }
    }

    for (const p of players) {
      if (p.id === selfId || p.status === 0) continue;
      const [mx, my] = toMap(p.x, p.z);
      if (p.ad) {
        // The ad circus: a small gold ring — a landmark, not a contact.
        ctx.strokeStyle = "#f4c542";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(mx, my, 3, 0, Math.PI * 2);
        ctx.stroke();
        continue;
      }
      if (p.ace) {
        // The comeback ace reads gold, drawn as a diamond (roadmap #14).
        ctx.fillStyle = "#ffd700";
        ctx.save();
        ctx.translate(mx, my);
        ctx.rotate(Math.PI / 4);
        ctx.fillRect(-3, -3, 6, 6);
        ctx.restore();
        continue;
      }
      if (p.nemesis) {
        // Your nemesis burns orange and a little larger (roadmap #21).
        ctx.fillStyle = "#ff9a3a";
        ctx.fillRect(mx - 3, my - 3, 6, 6);
        continue;
      }
      ctx.fillStyle = p.team === 0 ? "#ff6b6b" : "#6b9bff";
      ctx.fillRect(mx - 2, my - 2, 4, 4);
    }

    if (selfPos) {
      const [mx, my] = toMap(selfPos.x, selfPos.z);
      ctx.save();
      ctx.translate(mx, my);
      ctx.rotate(-selfYaw + Math.PI / 2);
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.moveTo(0, -5);
      ctx.lineTo(4, 5);
      ctx.lineTo(-4, 5);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // Wind sock: an arrow pointing downwind plus the speed, so pilots know
    // which way their bombs will drift.
    const wind = this.wind ?? { x: 0, z: 0 };
    const wSpd = Math.hypot(wind.x, wind.z);
    if (wSpd >= 1) {
      const ang = Math.atan2(wind.z, wind.x);
      ctx.save();
      ctx.translate(16, 16);
      ctx.rotate(ang);
      ctx.strokeStyle = "#d5ecfa";
      ctx.fillStyle = "#d5ecfa";
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(-7, 0);
      ctx.lineTo(5, 0);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(9, 0);
      ctx.lineTo(3, -3.4);
      ctx.lineTo(3, 3.4);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = "#d5ecfa";
      ctx.font = "9px monospace";
      ctx.fillText(`${Math.round(wSpd)}`, 28, 19);
    }

    // Battleship plotting overlay: range rings around the manned turret,
    // the plotted target with its dispersion circle, shells in flight, and
    // fading rings where recent salvos landed.
    if (plot) {
      const [gx, gy] = toMap(plot.x, plot.z);
      ctx.strokeStyle = "rgba(255, 200, 120, 0.8)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.arc(gx, gy, toMapR(plot.maxR), 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(gx, gy, toMapR(plot.minR), 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      // Enemy ground targets: diamonds for emplacements, squares for the
      // high-value stuff (turrets, installations).
      for (const tg of plot.targets ?? []) {
        const [tx, ty] = toMap(tg.x, tg.z);
        ctx.fillStyle = "#ff5a4a";
        if (tg.big) {
          ctx.fillRect(tx - 2.5, ty - 2.5, 5, 5);
        } else {
          ctx.save();
          ctx.translate(tx, ty);
          ctx.rotate(Math.PI / 4);
          ctx.fillRect(-1.8, -1.8, 3.6, 3.6);
          ctx.restore();
        }
      }

      for (const im of plot.impacts ?? []) {
        const a = 1 - im.age / 6; // fade over 6 s
        if (a <= 0) continue;
        const [ix, iy] = toMap(im.x, im.z);
        ctx.strokeStyle = `rgba(255, 140, 60, ${a.toFixed(2)})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(ix, iy, 2 + im.age * 1.5, 0, Math.PI * 2);
        ctx.stroke();
      }

      for (const sh of plot.shells ?? []) {
        const [sx, sy] = toMap(sh.x, sh.z);
        ctx.fillStyle = "#ffd76b";
        ctx.fillRect(sx - 1.5, sy - 1.5, 3, 3);
      }

      // Spotter-called fire missions: pulsing green diamonds that fade as
      // the mark goes stale.
      const pulse = 4.5 + Math.sin(performance.now() / 220) * 1.5;
      for (const mk of plot.marks ?? []) {
        const rem = 1 - mk.age / (mk.ttl || 40);
        if (rem <= 0) continue;
        const a = Math.min(1, rem * 4); // fade over the last quarter
        const [mx, my] = toMap(mk.x, mk.z);
        ctx.strokeStyle = `rgba(123, 255, 138, ${a.toFixed(2)})`;
        ctx.lineWidth = 1.5;
        ctx.save();
        ctx.translate(mx, my);
        ctx.rotate(Math.PI / 4);
        ctx.strokeRect(-pulse, -pulse, pulse * 2, pulse * 2);
        ctx.restore();
      }

      if (plot.target) {
        const [tx, ty] = toMap(plot.target.x, plot.target.z);
        ctx.strokeStyle = plot.spotted ? "#7bff8a" : "#ffd76b";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(tx, ty, Math.max(2.5, toMapR(plot.spreadM)), 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(tx - 7, ty);
        ctx.lineTo(tx + 7, ty);
        ctx.moveTo(tx, ty - 7);
        ctx.lineTo(tx, ty + 7);
        ctx.stroke();
      }
    }
  }

  /**
   * Battleship main-battery plot table: a large chart centred on the manned
   * turret and zoomed to its firing envelope. Draws the terrain slice, the
   * shaded in-range band, range rings, contacts, spotter marks, shells in
   * flight, fading impacts, the committed target, and the draggable aim
   * reticle. `view` is {cx, cz, r} in world metres; `reticle` is the pending
   * (uncommitted) aim point or null.
   */
  drawPlot(field, view, overlay, reticle, selfPos, selfYaw) {
    const ctx = this.plotCtx;
    const size = this.el.plotCanvas.width;
    this.plotView = { cx: view.cx, cz: view.cz, r: view.r, size };
    const toMap = (x, z) => [
      ((x - view.cx) / view.r * 0.5 + 0.5) * size,
      ((z - view.cz) / view.r * 0.5 + 0.5) * size,
    ];
    const toMapR = (r) => (r / view.r) * (size / 2);

    // Parchment sheet, then the high-resolution paper-chart terrain. The chart
    // samples the heightfield per pixel — hill-shaded, with inked elevation
    // contours, a sandy coastline and pale isobath water — and is cached per
    // turret window, so each frame just blits the finished sheet.
    ctx.fillStyle = "#e7d9bd";
    ctx.fillRect(0, 0, size, size);
    const terrain = this.buildPlotTerrain(field, view);
    if (terrain) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(terrain, 0, 0, size, size);
    }

    const [gx, gy] = toMap(view.cx, view.cz);
    const maxRp = toMapR(overlay.maxR);
    const minRp = toMapR(overlay.minR);
    // Over-the-horizon (spotter-only) range collapses onto the direct ring when
    // the config leaves it unset.
    const spotRp = toMapR(Math.max(overlay.spotR ?? overlay.maxR, overlay.maxR));
    const hasSpotBand = spotRp > maxRp + 0.5;

    // A small ink-on-paper label: a pale parchment halo behind dark lettering so
    // the text stays legible over both land and water.
    const inkLabel = (text, x, y, color) => {
      ctx.lineWidth = 3;
      ctx.lineJoin = "round";
      ctx.strokeStyle = "rgba(244, 236, 218, 0.92)";
      ctx.strokeText(text, x, y);
      ctx.fillStyle = color;
      ctx.fillText(text, x, y);
    };

    // Dim everything outside the outermost (spotter) range so the reachable
    // water reads as the live part of the chart.
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, size, size);
    ctx.arc(gx, gy, spotRp, 0, Math.PI * 2, true);
    ctx.fillStyle = "rgba(22, 30, 22, 0.34)";
    ctx.fill("evenodd");
    ctx.restore();
    // Faint green wash over the direct-fire band.
    ctx.beginPath();
    ctx.arc(gx, gy, maxRp, 0, Math.PI * 2);
    ctx.arc(gx, gy, minRp, 0, Math.PI * 2, true);
    ctx.fillStyle = "rgba(70, 150, 90, 0.10)";
    ctx.fill("evenodd");
    // Cooler wash over the spotter-only band: in reach, but only with a plane
    // over the target.
    if (hasSpotBand) {
      ctx.beginPath();
      ctx.arc(gx, gy, spotRp, 0, Math.PI * 2);
      ctx.arc(gx, gy, maxRp, 0, Math.PI * 2, true);
      ctx.fillStyle = "rgba(60, 110, 170, 0.10)";
      ctx.fill("evenodd");
    }
    // Dead zone inside minimum range.
    ctx.beginPath();
    ctx.arc(gx, gy, minRp, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(150, 50, 40, 0.20)";
    ctx.fill();

    // Distance graticule every kilometre, inked faintly onto the chart.
    ctx.strokeStyle = "rgba(70, 52, 28, 0.16)";
    ctx.lineWidth = 1;
    for (let rr = 1000; rr < (overlay.spotR ?? overlay.maxR); rr += 1000) {
      ctx.beginPath();
      ctx.arc(gx, gy, toMapR(rr), 0, Math.PI * 2);
      ctx.stroke();
    }
    // Envelope rings: a solid amber direct-fire ring, the min-range ring, and a
    // dashed navy spotter ring marking over-the-horizon reach.
    ctx.strokeStyle = "rgba(176, 110, 30, 0.92)";
    ctx.lineWidth = 2.5;
    ctx.setLineDash([7, 5]);
    ctx.beginPath();
    ctx.arc(gx, gy, maxRp, 0, Math.PI * 2);
    ctx.stroke();
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(gx, gy, minRp, 0, Math.PI * 2);
    ctx.stroke();
    if (hasSpotBand) {
      ctx.strokeStyle = "rgba(34, 78, 132, 0.9)";
      ctx.beginPath();
      ctx.arc(gx, gy, spotRp, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.font = "bold 13px monospace";
    ctx.textAlign = "center";
    inkLabel(`${(overlay.maxR / 1000).toFixed(1)} km`, gx, gy - maxRp - 7, "#8a4d12");
    if (hasSpotBand) {
      inkLabel(`SPOTTER ${((overlay.spotR ?? overlay.maxR) / 1000).toFixed(1)} km`, gx, gy - spotRp - 7, "#1f4f86");
    }
    ctx.textAlign = "left";

    // Friendly/enemy ships within the window — inked hulls with a dark outline
    // so they sit on the paper rather than wash out against it.
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(36, 26, 12, 0.7)";
    for (const s of this.ships ?? []) {
      const [mx, my] = toMap(s.x, s.z);
      ctx.fillStyle = s.alive === false ? "#7b7468" : s.team === 0 ? "#c23b3b" : "#2f6fb0";
      if (s.kind === 0 || s.kind === 2) { ctx.fillRect(mx - 5, my - 2.5, 10, 5); ctx.strokeRect(mx - 5, my - 2.5, 10, 5); }
      else { ctx.fillRect(mx - 2.5, my - 1.5, 5, 3); ctx.strokeRect(mx - 2.5, my - 1.5, 5, 3); }
    }
    // Aircraft contacts (helps the gunner see friendly spotters loitering).
    for (const b of overlay.blips ?? []) {
      const [mx, my] = toMap(b.x, b.z);
      ctx.fillStyle = b.team === 0 ? "#d23b2f" : "#2f6fb0";
      ctx.beginPath();
      ctx.moveTo(mx, my - 3.2); ctx.lineTo(mx + 3.2, my); ctx.lineTo(mx, my + 3.2); ctx.lineTo(mx - 3.2, my);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    // Spotter bubbles: each friendly plane reveals enemy ground inside its
    // spot radius. Live green circles so the gunner watches the fog lift (and
    // re-close) as teammates fly over the target area.
    const spotViewRp = toMapR(overlay.spotViewR ?? 0);
    if (spotViewRp > 1) {
      for (const s of overlay.spotters ?? []) {
        const [mx, my] = toMap(s.x, s.z);
        ctx.strokeStyle = "rgba(40, 140, 70, 0.6)";
        ctx.fillStyle = "rgba(90, 190, 120, 0.13)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(mx, my, spotViewRp, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    // The manned ship at the chart centre, pointing along the gun's bearing.
    if (selfPos) {
      const [mx, my] = toMap(selfPos.x, selfPos.z);
      ctx.save();
      ctx.translate(mx, my);
      ctx.rotate(-(selfYaw ?? 0) + Math.PI / 2);
      ctx.fillStyle = "#caa12e";
      ctx.strokeStyle = "rgba(36, 26, 12, 0.85)";
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.rect(-3.5, -9, 7, 18);
      ctx.moveTo(0, -13);
      ctx.lineTo(4, -8);
      ctx.lineTo(-4, -8);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    // Enemy ground targets, stuck into the chart as pins: bigger orange pins
    // for the high-value installations, smaller red pins for emplacements.
    for (const tg of overlay.targets ?? []) {
      const [tx, ty] = toMap(tg.x, tg.z);
      if (tg.big) this.drawPin(ctx, tx, ty, 8, "#ff7a43", "#7c2410");
      else this.drawPin(ctx, tx, ty, 5, "#ef4536", "#7c1b12");
    }

    // Fading impact rings where recent salvos landed.
    for (const im of overlay.impacts ?? []) {
      const a = 1 - im.age / 6;
      if (a <= 0) continue;
      const [ix, iy] = toMap(im.x, im.z);
      ctx.strokeStyle = `rgba(196, 86, 28, ${a.toFixed(2)})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(ix, iy, 4 + im.age * 4, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Surface hulls from the fog-filtered snapshot: warships ink as hulls,
    // armor as small blocks. (Unspotted enemies never reach the client at
    // all, so the chart draws every contact it's handed.) Shells in flight
    // are deliberately NOT charted — a plotting table marks contacts and
    // fall of shot (the impact rings), not rounds in the air.
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(36, 26, 12, 0.7)";
    for (const h of overlay.hulls ?? []) {
      const [mx, my] = toMap(h.x, h.z);
      ctx.fillStyle = h.team === 0 ? "#c23b3b" : "#2f6fb0";
      if (h.ship) {
        ctx.fillRect(mx - 4.5, my - 2.5, 9, 5);
        ctx.strokeRect(mx - 4.5, my - 2.5, 9, 5);
      } else {
        ctx.fillRect(mx - 2, my - 2, 4, 4);
        ctx.strokeRect(mx - 2, my - 2, 4, 4);
      }
    }

    // Spotter-called fire missions: green pins that pulse and fade as the mark
    // goes stale.
    const pulse = 7 + Math.sin(performance.now() / 220) * 1.3;
    for (const mk of overlay.marks ?? []) {
      const rem = 1 - mk.age / (mk.ttl || 40);
      if (rem <= 0) continue;
      const a = Math.min(1, rem * 4);
      const [mx, my] = toMap(mk.x, mk.z);
      ctx.save();
      ctx.globalAlpha = a;
      this.drawPin(ctx, mx, my, pulse, "#5fe07a", "#1f6b34");
      ctx.restore();
    }

    // The committed target: dispersion circle (green spotted / amber not) and
    // a dispersion circle (green when a spotter tightens it, amber otherwise)
    // and a gold pin marking the committed point.
    if (overlay.target) {
      const [tx, ty] = toMap(overlay.target.x, overlay.target.z);
      ctx.strokeStyle = overlay.spotted ? "#2f9e4a" : "#b6791e";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(tx, ty, Math.max(4, toMapR(overlay.spreadM)), 0, Math.PI * 2);
      ctx.stroke();
      this.drawPin(ctx, tx, ty, 8, "#ffd357", "#9a6a16");
    }

    // The draggable aim reticle (pending, not yet committed): a crisp inked
    // crosshair with a dashed bearing line back to the ship and an
    // in/out-of-range tint that reads against the parchment.
    if (reticle) {
      const d = Math.hypot(reticle.x - view.cx, reticle.z - view.cz);
      const inRange = d >= overlay.minR && d <= overlay.maxR;
      const [rx, ry] = toMap(reticle.x, reticle.z);
      const ink = inRange ? "#15304a" : "#c0291b";
      ctx.strokeStyle = ink;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.moveTo(gx, gy);
      ctx.lineTo(rx, ry);
      ctx.stroke();
      ctx.setLineDash([]);
      // Pale halo under the reticle so it never disappears over dark water.
      ctx.lineWidth = 4;
      ctx.strokeStyle = "rgba(244, 236, 218, 0.85)";
      ctx.beginPath();
      ctx.arc(rx, ry, Math.max(5, toMapR(overlay.spreadM)), 0, Math.PI * 2);
      ctx.moveTo(rx - 14, ry); ctx.lineTo(rx + 14, ry);
      ctx.moveTo(rx, ry - 14); ctx.lineTo(rx, ry + 14);
      ctx.stroke();
      ctx.lineWidth = 2;
      ctx.strokeStyle = ink;
      ctx.beginPath();
      ctx.arc(rx, ry, Math.max(5, toMapR(overlay.spreadM)), 0, Math.PI * 2);
      ctx.moveTo(rx - 14, ry); ctx.lineTo(rx + 14, ry);
      ctx.moveTo(rx, ry - 14); ctx.lineTo(rx, ry + 14);
      ctx.stroke();
    }
  }

  /** A map pin stuck into the chart: a soft shadow on the paper, a tapering
   *  stem down to the exact target point, and a glossy round head. The tip sits
   *  precisely at (x, y) so the pin marks the spot it's planted on. */
  drawPin(ctx, x, y, size, color, dark) {
    ctx.save();
    // Shadow cast onto the paper, offset toward the lower-right.
    ctx.fillStyle = "rgba(38, 28, 12, 0.26)";
    ctx.beginPath();
    ctx.ellipse(x + size * 0.55, y + size * 0.16, size * 0.95, size * 0.36, 0, 0, Math.PI * 2);
    ctx.fill();

    const cy = y - size * 2.15; // head centre, lifted off the surface
    const rh = size;
    // Tapering stem from the tip up to the head.
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - rh * 0.6, cy + rh * 0.1);
    ctx.lineTo(x + rh * 0.6, cy + rh * 0.1);
    ctx.closePath();
    ctx.fillStyle = dark;
    ctx.fill();

    // Glossy spherical head.
    const grad = ctx.createRadialGradient(
      x - rh * 0.35, cy - rh * 0.4, rh * 0.12, x, cy, rh * 1.1,
    );
    grad.addColorStop(0, "#ffffff");
    grad.addColorStop(0.28, color);
    grad.addColorStop(1, dark);
    ctx.beginPath();
    ctx.arc(x, cy, rh, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.lineWidth = Math.max(1, size * 0.16);
    ctx.strokeStyle = dark;
    ctx.stroke();

    // Specular highlight.
    ctx.beginPath();
    ctx.arc(x - rh * 0.32, cy - rh * 0.34, rh * 0.26, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
    ctx.fill();
    ctx.restore();
  }

  /** Render the turret's chart window as a high-resolution paper map: the
   *  heightfield sampled per pixel, hill-shaded under a north-west sun, with
   *  inked elevation contours, a sandy coastline and pale isobath water. Cached
   *  per window so it builds once when you take the seat, not every frame. */
  buildPlotTerrain(field, view) {
    const dst = this.el.plotCanvas.width;
    const res = Math.min(dst, 896); // sampling resolution (capped for build cost)
    const key = `${view.cx.toFixed(1)}|${view.cz.toFixed(1)}|${view.r.toFixed(2)}|${res}|${field.seed}`;
    if (this._plotTerrain && this._plotTerrain.key === key) return this._plotTerrain.canvas;

    const c = document.createElement("canvas");
    c.width = c.height = res;
    const ctx = c.getContext("2d");
    const img = ctx.createImageData(res, res);
    const data = img.data;

    const span = view.r * 2;
    const step = span / res;
    const x0 = view.cx - view.r;
    const z0 = view.cz - view.r;
    const fieldR2 = field.radius * field.radius;

    // 1) Sample the heightfield once per pixel.
    const H = new Float32Array(res * res);
    for (let py = 0; py < res; py++) {
      const z = z0 + (py + 0.5) * step;
      for (let px = 0; px < res; px++) {
        H[py * res + px] = field.heightAt(x0 + (px + 0.5) * step, z);
      }
    }

    // Topographic (elevation) and bathymetric (depth) colour ramps.
    const landStops = [
      [0, [226, 214, 174]],   // sand
      [8, [201, 213, 160]],   // pale-green lowland
      [45, [213, 204, 150]],  // green-tan
      [110, [207, 183, 135]], // tan
      [180, [199, 171, 140]], // brown
      [240, [208, 195, 172]], // rocky highland
    ];
    const waterStops = [
      [0, [205, 227, 233]],  // shoal
      [3, [181, 213, 227]],
      [8, [150, 193, 215]],
      [14, [118, 169, 201]], // deep
    ];
    const ramp = (stops, v) => {
      let i = 0;
      while (i < stops.length - 1 && v > stops[i + 1][0]) i++;
      const a = stops[i], b = stops[Math.min(i + 1, stops.length - 1)];
      const t = b[0] === a[0] ? 0 : clamp01((v - a[0]) / (b[0] - a[0]));
      return [
        a[1][0] + (b[1][0] - a[1][0]) * t,
        a[1][1] + (b[1][1] - a[1][1]) * t,
        a[1][2] + (b[1][2] - a[1][2]) * t,
      ];
    };

    // Relief light from the north-west, with vertical exaggeration so the gentle
    // island still reads in three dimensions on the sheet.
    const lx = -0.62, ly = 0.76, lz = -0.62;
    const VE = 2.4;

    for (let py = 0; py < res; py++) {
      for (let px = 0; px < res; px++) {
        const i = py * res + px;
        const e = H[i];
        const wx = x0 + (px + 0.5) * step, wz = z0 + (py + 0.5) * step;
        const off = wx * wx + wz * wz > fieldR2; // beyond the world disc → open sea

        // Gradient via central differences (edges clamped).
        const xl = px > 0 ? H[i - 1] : e, xr = px < res - 1 ? H[i + 1] : e;
        const zu = py > 0 ? H[i - res] : e, zd = py < res - 1 ? H[i + res] : e;
        const hx = (xr - xl) / (2 * step);
        const hz = (zd - zu) / (2 * step);
        const gradMag = Math.hypot(hx, hz);

        const nx = -hx * VE, nz = -hz * VE, ny = 1;
        const ninv = 1 / Math.hypot(nx, ny, nz);
        let shade = (nx * lx + ny * ly + nz * lz) * ninv;
        shade = 0.96 + shade * 0.22;

        let r, g, b;
        if (e < 0 || off) {
          const d = off ? 14 : -e;
          [r, g, b] = ramp(waterStops, d);
          if (!off) {
            // Isobaths every 4 m, drawn at a uniform on-screen width.
            const ci = 4;
            const f = Math.abs(e / ci - Math.round(e / ci)) * ci;
            const band = gradMag * step * 0.55 + 1e-4;
            if (f < band) {
              const t = (1 - f / band) * 0.5;
              r += (96 - r) * t; g += (150 - g) * t; b += (182 - b) * t;
            }
          }
          const ws = 0.99 + (shade - 1) * 0.3; // very gentle shading on water
          r *= ws; g *= ws; b *= ws;
        } else {
          [r, g, b] = ramp(landStops, e);
          r *= shade; g *= shade; b *= shade;
          // Elevation contours every 30 m, inked sepia.
          const ci = 30;
          const f = Math.abs(e / ci - Math.round(e / ci)) * ci;
          const band = gradMag * step * 0.5 + 1e-4;
          if (f < band) {
            const t = (1 - f / band) * 0.45;
            r += (120 - r) * t; g += (96 - g) * t; b += (60 - b) * t;
          }
        }

        // Always-crisp coastline ink at the waterline.
        const coastBand = gradMag * step * 0.7 + 1.1;
        if (!off && Math.abs(e) < coastBand) {
          const t = (1 - Math.abs(e) / coastBand) * 0.55;
          r += (104 - r) * t; g += (82 - g) * t; b += (50 - b) * t;
        }

        // Paper grain.
        const gr = ((((px * 73856093) ^ (py * 19349663)) >>> 0) & 0xff) / 255 - 0.5;
        r += gr * 9; g += gr * 9; b += gr * 8;

        const o = i * 4;
        data[o] = r < 0 ? 0 : r > 255 ? 255 : r;
        data[o + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
        data[o + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
        data[o + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);

    // A faint world-aligned graticule and an aged vignette finish the sheet.
    ctx.strokeStyle = "rgba(70, 52, 28, 0.08)";
    ctx.lineWidth = 1;
    for (let gw = Math.ceil(x0 / 1000) * 1000; gw < x0 + span; gw += 1000) {
      const sx = ((gw - x0) / span) * res;
      ctx.beginPath(); ctx.moveTo(sx, 0); ctx.lineTo(sx, res); ctx.stroke();
    }
    for (let gw = Math.ceil(z0 / 1000) * 1000; gw < z0 + span; gw += 1000) {
      const sy = ((gw - z0) / span) * res;
      ctx.beginPath(); ctx.moveTo(0, sy); ctx.lineTo(res, sy); ctx.stroke();
    }
    const vig = ctx.createRadialGradient(res / 2, res / 2, res * 0.34, res / 2, res / 2, res * 0.72);
    vig.addColorStop(0, "rgba(60, 44, 22, 0)");
    vig.addColorStop(1, "rgba(46, 32, 14, 0.22)");
    ctx.fillStyle = vig;
    ctx.fillRect(0, 0, res, res);

    this._plotTerrain = { key, canvas: c };
    return c;
  }
}

// Hand-coded 48×20 side-profile silhouettes for the kill-confirm banner, one
// per hangar slot — filled rects only, in the HUD gold. Deliberately NOT
// rasterized from planes.js geometry: an icon reads better than a render.
function drawPlaneSilhouette(ctx, planeIdx) {
  ctx.clearRect(0, 0, 48, 20);
  ctx.fillStyle = "#f4c542";
  const rects = {
    0: [[6, 9, 34, 3], [40, 9, 4, 3], [18, 6, 8, 3], [6, 4, 3, 6], [20, 11, 12, 2]], // Spitfire: slim turnfighter
    1: [[4, 9, 38, 3], [42, 10, 4, 2], [16, 6, 9, 3], [4, 3, 3, 7], [18, 11, 16, 2]], // Mustang: long-nosed B&Z
    2: [[4, 8, 38, 4], [14, 5, 8, 3], [4, 3, 4, 8], [12, 12, 8, 3], [28, 12, 8, 3]], // P-38: twin-engine heavy
    3: [[2, 8, 42, 4], [2, 2, 4, 9], [10, 12, 6, 3], [19, 12, 6, 3], [28, 12, 6, 3], [37, 12, 6, 3]], // B-17: four engines
    4: [[6, 8, 34, 3], [40, 8, 4, 3], [17, 5, 8, 3], [6, 3, 3, 7], [16, 12, 14, 2], [20, 15, 10, 2]], // Avenger: fish under the belly
    5: [[6, 9, 36, 3], [42, 9, 4, 2], [14, 6, 7, 3], [6, 4, 4, 6], [2, 10, 4, 2]], // F-104: jet, intake tail-pipe
    8: [[6, 9, 34, 3], [40, 9, 4, 3], [24, 6, 6, 3], [6, 3, 3, 7], [22, 11, 10, 2]], // Bf 109: canopy far forward, small wing
    9: [[6, 9, 32, 3], [38, 8, 6, 5], [20, 6, 7, 3], [6, 4, 3, 6], [22, 11, 12, 2]], // Fw 190: the fat radial
    10: [[5, 9, 36, 3], [41, 9, 4, 3], [20, 6, 7, 3], [5, 3, 3, 7], [21, 11, 12, 2], [34, 11, 6, 2]], // Warhawk: the chin scoop
    11: [[4, 9, 36, 3], [40, 8, 4, 5], [14, 6, 7, 3], [4, 3, 3, 7], [18, 12, 8, 2], [26, 11, 8, 2]], // Corsair: bent wing, cockpit aft
    12: [[4, 8, 38, 3], [14, 5, 14, 3], [4, 3, 3, 8], [20, 11, 8, 3], [30, 11, 8, 3]], // Bf 110: long greenhouse, twin
    13: [[3, 8, 40, 4], [30, 5, 6, 3], [3, 2, 4, 9], [16, 12, 8, 3], [28, 12, 8, 3]], // Havoc: gun nose, twin
    14: [[4, 8, 38, 4], [36, 6, 8, 5], [4, 2, 4, 9], [16, 12, 10, 3], [20, 15, 12, 2]], // Ju 88: glass nose, fish
    15: [[2, 8, 42, 4], [38, 7, 6, 5], [2, 2, 4, 9], [14, 12, 8, 3], [26, 12, 8, 3]], // He 111: glass nose, long
    16: [[6, 9, 36, 3], [42, 9, 4, 2], [20, 6, 7, 3], [6, 4, 4, 6], [18, 12, 12, 3]], // Me 262: jet pods under
  };
  for (const [x, y, w, h] of rects[planeIdx] ?? rects[0]) ctx.fillRect(x, y, w, h);
}

function esc(s) {
  return String(s).replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);
}

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

// Move `cur` toward `target` at `rate` per second, frame-rate independent.
function approach(cur, target, rate, dt) {
  return cur + (target - cur) * (1 - Math.exp(-rate * dt));
}
